// CONSOLE view: a mixer-style overview of every level-settable node, laid out as
// vertical channel strips. Each strip shows the set fader level (amber ladder),
// the live signal meter (green→red, only while Live sync streams), mute, gain, and
// EQ. A per-strip SENDS rack (between the head and the fader zone) gives every
// strip always-available columns for all of its MIX/FX sends — an enable chip, a
// PRE button and a vertical mini-fader per send — plus a SEND PAN popover.
// Edits go straight onto the plan and through the shared change funnel, so Live
// sync mirrors them to the device exactly like the graph/inspector do.

import type { DeviceModel } from "../models/types";
// Aliased: `ref` is a local in several builders here (a SendColRef), and the port-ref
// helper has to stay reachable inside them.
import { ref as portRef } from "../models/types";
import { defaultPlan } from "../models/initial-state";
import { sideLabel } from "../models";
import {
  isPlainRecord,
  jackKey,
  LEVEL_MAX_DB,
  LEVEL_MIN_DB,
  LEVEL_OFF_DB,
  sendConnection,
  SSMCS_INITIAL,
  type JackSide,
  type NodeParams,
  type Plan,
  type PlanConnection,
  type SsmcsParams,
} from "../core/plan";
import { LEVEL_POS_MAX, levelToPos, posToLevel, stepLevel } from "../core/levels";
import { hiZPatch, inputOnRefused, jackGainRange, jackIndex } from "../core/input-lock";
import type { InputSwitch } from "../core/input-lock";
import {
  defaultTapKey,
  hasMeter,
  isStereoTap,
  METER_FLOOR_DB,
  METER_GREEN_TOP_DB,
  METER_YELLOW_TOP_DB,
  MeterStore,
  subscribeMeters,
  tapAddrs,
  tapFor,
  tapsFor,
  type MeterTap,
} from "../core/meters";
import { loadJson, saveJson } from "../core/storage";
import { COMP_EQ_COMP_FIRST, planColorHex } from "../core/control/params";
import { FX_CHANNEL_NODE_INDEX, fxEffectTypes, resolveFxEffectType } from "../core/control/fx-effect";
import { dynOpenLabel } from "./dyn-registry";
import type { DynKind } from "./dyn-registry";
import { markMidi } from "./midi-learn";
import type { MidiLearnHooks } from "./midi-learn";
import {
  channelEqUnavailable,
  formatRate,
  nodeRateDisabled,
  insertFxRateLock,
  insertFxCensus,
  insertFxFree,
  insertFxMenu,
  isMonitorBus,
  type InsertFxCensus,
} from "../core/constraints";
import {
  busBalance,
  channelControl,
  channelDynamics,
  cueAddrOf,
  effectiveInsertFx,
  hasEq,
  inputStageReaders,
  insertFxControl,
  isStereoChannel,
} from "../core/control/translate";
import { displayedSide, shownStage } from "../core/stereo-side";
import { inputShare } from "./input-share";
import { nodeParamContestPath } from "../core/plan-history";
import {
  alignLinkedSendPans,
  panLinkedSendPan,
  INSERT_FX_PAIR_KEYS,
  isBalLinkedPair,
  isNodeInactive,
  isStereoLinkedPair,
  mirrorLinkedPair,
  mirrorLinkedInsertFx,
  mixSendLocks,
  pairSharesNodeKey,
  partnerChannel,
  sendTapWritable,
  withLinkedPartner,
} from "../core/routing";
import { INSERT_FX_NONE, insertFxEngaged, insertFxSelected } from "../core/control/params";
import { parkOutgoingInsertFxParams, seedInsertFxParams } from "./insert-fx-model";
import { insertFxScreenFamily } from "./insert-fx-screen";
import {
  D_GAIN_MAX_DB,
  D_GAIN_MIN_DB,
  DELAY_TIME_GRID_MS,
  DELAY_TIME_MAX_MS,
  DELAY_TIME_MIN_MS,
  delayTimeToVd,
  formatLevelNumber,
  PAN_MAX,
  PAN_MIN,
  PHONES_LEVEL_DEFAULT,
  PHONES_LEVEL_MAX,
  PHONES_LEVEL_MIN,
  vdToDelayTime,
} from "../core/control/vd";
// MAIN_BUS (the STEREO master, every channel's fixed main send) and the
// MIX/FX send targets are shared with the MIDI control catalog.
import { controlId, MAIN_BUS, SEND_TARGETS, SSMCS_SC_SCOPE, type SendTarget } from "../core/midi/controls";
import { setLevelText, setNamedText, setTip, spoken, tipOf } from "./glyph";
import {
  el,
  focusables,
  onWheelStep,
  popLeft,
  popTop,
  preserveFocus,
  scrubFloat,
  tabbable,
  trackDrag,
  KNOB_TRAVEL_PX,
} from "./dom";
import { isChord } from "./keys";
import { fineActive, fineTag } from "./fine";
import { t } from "../i18n";

// Full destination name (header readout + SEND PAN popover) and the short chip
// label (rack column). SEND_TARGETS fixes the column order: FX 1, FX 2, MIX 1, MIX 2.
const SEND_LABEL: Record<SendTarget, string> = {
  "bus.mix1": "MIX 1",
  "bus.mix2": "MIX 2",
  "bus.fx1": "FX 1",
  "bus.fx2": "FX 2",
};
const SEND_SHORT: Record<SendTarget, string> = {
  "bus.mix1": "M1",
  "bus.mix2": "M2",
  "bus.fx1": "F1",
  "bus.fx2": "F2",
};

// The INS FX face on a strip, and the same switch repeated in the popover. Named
// because the two are one control in two places and each has to follow the other
// without a render — a render closes the popover, so the switch would answer a press
// by vanishing. `.con-chip` first so the pair rule (`.paired`) and every chip state
// still reach it.
const IFX_FACE_CLS = "con-chip con-ifxface";
// The face with nothing in it. A dashed rim, so a strip holding no effect is told
// apart from one holding a bypassed effect without asking the reader to compare two
// unlit faces — and told apart by the rim rather than by colour alone.
const IFX_VACANT_CLS = "con-chip con-ifxface vacant";

// A fader scale: how a dB maps to/from travel (toFrac/fromFrac), how the keyboard
// steps it (step), and its ruler ticks. NORMAL_RANGE is the only instance (the
// level_gain grid); it is threaded through the level helpers so the scale is
// defined in one place.
interface LevelRange {
  min: number;
  max: number;
  off: number;
  toFrac: (db: number) => number;
  fromFrac: (frac: number) => number;
  step: (base: number, delta: number) => number;
  ticks: number[];
}

// The level_gain range: detents spaced evenly by grid index (not by dB), keyboard
// walks one detent per press, ticks are all real detents.
const NORMAL_RANGE: LevelRange = {
  min: LEVEL_MIN_DB,
  max: LEVEL_MAX_DB,
  off: LEVEL_OFF_DB,
  toFrac: (db) => levelToPos(db) / LEVEL_POS_MAX,
  fromFrac: (f) => posToLevel(Math.round(f * LEVEL_POS_MAX)),
  step: (base, delta) => stepLevel(base, delta),
  ticks: [10, 5, 0, -5, -10, -20, -40, -96],
};

function dbToFrac(db: number, r: LevelRange): number {
  return r.toFrac(db);
}
function fracToDb(frac: number, r: LevelRange): number {
  return r.fromFrac(Math.max(0, Math.min(1, frac)));
}
function meterFrac(dbfs: number, r: LevelRange): number {
  // The meter shares the strip's fader ruler: a dBFS reading lights to the same
  // travel as the matching dB tick. Normalised to the ladder's span — bottom at the
  // scale's lowest tick, top at the 0 dB mark — so the fill and the ticks line up.
  const floor = r.toFrac(r.ticks[r.ticks.length - 1]);
  const span = r.toFrac(0) - floor;
  return span <= 0 ? 0 : Math.max(0, Math.min(1, (r.toFrac(Math.min(dbfs, 0)) - floor) / span));
}
function fmtDb(db: number, r: LevelRange): { text: string; off: boolean } {
  // A non-finite level (corrupt plan that slipped past validation) reads as off
  // rather than throwing on .toFixed below.
  if (!Number.isFinite(db) || db < r.min) return { text: "-∞", off: true };
  return { text: formatLevelNumber(db), off: false };
}

// A level fader's accessible value: the readout's text with its unit, or "off (-∞)" below
// the floor, so −∞ and the floor detent, and the detents either side of 0 dB, are read
// apart where the rounded aria-valuenow is one number for both. Shared by the main fader
// and the rack columns, which put `prefix` ("PRE, ") in front of a pre-fader send.
function levelValueText(db: number, r: LevelRange, prefix = ""): string {
  const f = fmtDb(db, r);
  return f.off ? "off (-∞)" : prefix + f.text + " dB";
}

// The range a level fader's rounded aria-valuenow runs over, in place of the slider
// role's default 0..100: −∞ rounds to the floor's integer, and the top is the scale's
// maximum.
function setLevelRange(fader: HTMLElement, r: LevelRange): void {
  fader.setAttribute("aria-valuemin", String(Math.round(r.off)));
  fader.setAttribute("aria-valuemax", String(r.max));
}

// A three-bar meter glyph (rising heights), coloured by the host's currentColor
// so it tracks the badge's amber (dark) / brown (light). Marks the meter-point
// badge apart from the send-tap chip.
function meterGlyph(): SVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 8 8");
  svg.setAttribute("width", "8");
  svg.setAttribute("height", "8");
  svg.setAttribute("aria-hidden", "true");
  svg.classList.add("mtr-ico");
  for (const [x, y, h] of [
    [0, 4, 4],
    [3.1, 2, 6],
    [6.2, 0, 8],
  ]) {
    const rect = document.createElementNS(ns, "rect");
    rect.setAttribute("x", String(x));
    rect.setAttribute("y", String(y));
    rect.setAttribute("width", "1.7");
    rect.setAttribute("height", String(h));
    rect.setAttribute("fill", "currentColor");
    svg.append(rect);
  }
  return svg;
}

// A readout caption (FADER / METER): a terse label above a readout value so the
// set-level cell and the live-meter cell read apart at a glance, not by colour alone.
function readCap(text: string): HTMLElement {
  const cap = el("span", "cap2");
  cap.textContent = text;
  return cap;
}

// The live-meter readout cell (METER caption + dBFS value, reset to "—" until the
// stream feeds it). Shared by both strip builders; the value element is returned so
// the caller can store it as the strip's readMtr.
function meterReadCell(): { cell: HTMLElement; value: HTMLElement } {
  const cell = el("div", "rd mtr");
  const value = el("div", "rv");
  value.textContent = "—";
  cell.append(readCap(t().console.readMeter), value);
  return { cell, value };
}

// A strip is a group named by its node, so a control inside it — named only for what it is
// ("MUTE", "PAN", "FX 1") — is announced with the channel it belongs to.
function nameStrip(strip: HTMLElement, m: { label: string }): void {
  strip.setAttribute("role", "group");
  strip.setAttribute("aria-label", spoken(m.label));
}

// Focus inside an open popover: the popover, the strip it belongs to, and the row as its
// identity and its position among the popover's tabbable rows (-1 where it is not one).
interface PopoverFocusMark {
  pop: "tap" | "pan" | "ifx";
  id: string;
  idx: number;
  ctl: string | undefined;
  sel: readonly string[];
}

interface StripModel {
  id: string;
  label: string;
  rail: string; // node kind → --rail-<kind>
  deviceName: string; // device CH SETTING name (plan.nodeNames), or ""
  isChannel: boolean;
  isMono: boolean;
  isBalance: boolean; // pan reads as a BALANCE (stereo / FX channel, or a BAL-linked pair)
  fadersOnly: boolean; // bus/mon/osc/master: always show their own level
  isOsc: boolean;
  isStream: boolean; // the STREAMING bus (meter-only, carries the DELAY chip + TIME knob)
  hasMute: boolean; // strips with a → STEREO send (CH / FX / MIX)
  hasEq: boolean; // channels + mix + stereo
  hasPhones: boolean; // monitor buses (PHONES 1 ↔ mon1, PHONES 2 ↔ mon2)
  meterOnly: boolean; // STREAMING: only a live meter, no fader / set-level readout
  inactive: boolean; // node master is off (dim the strip; shared isNodeInactive predicate)
  range: LevelRange;
}

// The scribble power LED's binding: the current on-state, a flip, and the MIDI id it
// arms. Null for STREAMING (no on/off param).
interface PowerSpec {
  on: boolean;
  /** Flips the flag and returns the node-parameter keys it wrote. */
  toggle: () => readonly string[];
  midiId: string;
}

// One metered channel within a strip's meter column. A mono strip has a single lane;
// a stereo strip (whose tap carries an R address) has two, L and R, sharing one ladder
// frame and one OVER frame but each with its own bar column and clip cell.
// v/pk: live level/peak ballistics; over: clip latch ballistic. lv/lpk/lov: last value
// written to the DOM (-1 = none yet) so paintMeters can skip unchanged writes. live =
// lane is animating (its `live` class promotes the shade/peak to compositor layers).
interface MeterLane {
  col: HTMLElement; // bar column (bar + shade + peak); `live` class gates its layers
  shade: HTMLElement;
  peak: HTMLElement;
  clip: HTMLElement; // this channel's OVER latch cell
  v: number;
  pk: number;
  over: number;
  lv: number;
  lpk: number;
  lov: number;
  live: boolean;
}

interface StripRef {
  m: StripModel;
  // The strip's root element, so a device-follow direct change can rebuild just this
  // strip in place (refreshStrip) instead of re-rendering the whole console.
  root: HTMLElement;
  lanes: MeterLane[]; // 1 (mono) or 2 (stereo L, R)
  // Fader controls — absent on a meter-only strip (STREAMING), which has no fader.
  cap?: HTMLElement;
  fader?: HTMLElement;
  readDb?: HTMLElement;
  readMtr: HTMLElement; // live meter value cell (the selected tap's dBFS, peak of L/R)
  tap: MeterTap | null; // the resolved tap this strip's meter shows (fixed per render)
  // lmtr = last meter readout written (deci-dB; 1 = sentinel "none written").
  sig: { lmtr: number };
  // SENDS rack: the per-send column faders — kept so a linked partner's rack
  // fader can be mirrored in place, like the main fader. The header readout and the
  // collapsed dots are reached via `root` when the global collapse toggles.
  sendCols?: SendColRef[];
}

// One send column's fader in a strip's SENDS rack, keyed by send target so a
// linked partner strip can mirror the matching column live (mirrorPartnerSend).
interface SendColRef {
  target: SendTarget;
  fader: HTMLElement;
  cap: HTMLElement;
}

interface KnobSpec {
  get: () => number;
  set: (v: number) => void;
  min: number;
  max: number;
  step: number;
  /** Step while Shift is held (fine-tuning mode). Only params with a
   *  device-verified fine grid set it (the STREAMING TIME knob, 0.02 ms).
   *  The printed legend sits in the whitespace above the knob (style.css
   *  `.con-gain .fine-tag`) — a knob with content directly above (a stacked
   *  PAN/BAL row) would need a new anchor before opting in. */
  fine?: number;
  /** The grid a drag and a reset land on, finer than `step`, rounded to halfway up. A key
   *  or a wheel notch then moves the held value by exactly `step` (or `fine`), so a value
   *  off the grid stays off it. Absent, a value lands on `step` itself (the STREAMING TIME
   *  knob sets 0.02 ms). */
  grid?: number;
  format: (v: number) => string;
  reset: number;
  /** Indicator angle (deg) for a value; default is a -135°..+135° sweep over the
   *  range. Override to place specific values (e.g. PHONES 2/8) at the horizontal. */
  angle?: (v: number) => number;
  /** When set, the knob shows its value but cannot be edited (a device-locked
   *  control, e.g. a FIXED-bus send pan). The string is the disabled tooltip. */
  readonlyTitle?: string;
  /** Repaints, in place, another knob showing the value this one just wrote. */
  mirror?: (knob: HTMLElement, val: HTMLElement) => void;
  /** With `readonlyTitle`: the knob still takes a MIDI learn, as a control the sample rate has
   *  taken away does — its assignment outlives the rate, and only the value is refused. */
  assignable?: boolean;
  /** The node-parameter keys `set` writes, for the change funnel's write witness.
   *  Absent for a knob that writes a wire's params instead. */
  keys?: readonly string[];
  /** How a linked pair's partner follows this knob: absent = always (the pair shares it),
   *  "pan" = in BAL only, "own" = never (the head amp). It decides whether an edit earns the
   *  partner rebuild — where the partner did not move, the rebuild only takes down what is
   *  open on it. */
  pairs?: "pan" | "own";
}

/** MIDI-learn integration. The contract is shared with the channel tuning screens
 *  (ui/midi-learn.ts): the MIDI window owns the mode / armed state and re-renders
 *  both surfaces when they change. */
export type ConsoleMidiHooks = MidiLearnHooks;

export interface ConsoleHooks {
  getModel: () => DeviceModel;
  getPlan: () => Plan;
  /** Whether MIC/LINE jack `jack` (0 = the first) has its Clip Safe holding the gain down, as the
   *  unit last announced it. */
  clipSafeEngaged?: (jack: number) => boolean;
  /** An edit changed the plan (mute / fader / EQ): flag dirty + schedule live sync.
   *  `written` names the contest keys the edit ASSERTED — its own and the ones a pair
   *  mirror carried — not only the ones whose value moved. A device read in flight
   *  arbitrates by authorship, so a write that lands on the value already there is
   *  invisible to it otherwise. `defaults` names, as (node, dotted path) pairs, the values the
   *  edit put in as a type's defaults rather than as anything the operator chose — an effect
   *  selection's seeded engine slots, on the strip and on a mirrored partner. */
  onChange: (written?: readonly string[], defaults?: ReadonlyArray<readonly [string, string]>) => void;
  /** The meter stream could not be registered. Bars stuck on the floor are
   *  indistinguishable from silence, so the host surfaces this rather than
   *  leaving a live session that quietly shows nothing. */
  onMeterError?: (message: string) => void;
  /** Open the GATE tuning screen for a MONO IN channel. */
  onOpenDynScreen?: (kind: DynKind, id: string) => void;
  /** Choose an FX channel's EFFECT TYPE: the plan takes the incoming type's factory values, as
   *  it does from the Inspector. */
  onSetFxEffectType: (id: string, type: number) => void;
  /** Show the other side of a stereo channel (its name area was pressed). */
  onSwitchSide?: (channelId: string, side: JackSide) => void;
  /** The [CUE] flags, which are outside the plan: a CUE chip reads and switches them here
   *  and commits nothing. */
  cue?: ConsoleCueHooks;
  midi?: ConsoleMidiHooks;
}

export interface ConsoleCueHooks {
  has: (node: string) => boolean;
  /** Whether anything is cued. */
  any: () => boolean;
  set: (node: string, on: boolean) => void;
  /** Whether a press on a CUE chip switches it (a Live-sync session is up). */
  enabled: () => boolean;
}

// The bars animate every frame; the numeric readout text is refreshed only every
// Nth painted frame so its text relayout/repaint isn't a per-frame cost.
const READOUT_EVERY = 5;

export class Console {
  private paintN = 0; // frame counter gating the throttled numeric readout
  private refs = new Map<string, StripRef>();
  /** Each knob's own repaint from its spec's getter — what the plan holds, shown in place. */
  private knobRepaint = new WeakMap<HTMLElement, () => void>();
  private factory: { id: string; plan: Plan } | null = null; // cached factory plan
  private headH = { key: "", px: 0 }; // cached MAIN-tab head height (key: model + hidden)
  // The insert-FX slot census of the build pass in progress. It is a whole-model
  // sweep and every strip's INS FX chip asks the same question of it, so it is taken
  // once by whichever entry point starts the pass (render / refreshStrip — a render
  // also builds every strip a second time in mainHeadHeight) and handed to
  // insertFxMenu, rather than swept per strip. Null means no pass set one, and the
  // menu falls back to sweeping for itself.
  private ifxCensus: InsertFxCensus | null = null;
  private store = new MeterStore();
  private unsub: (() => void) | null = null;
  private subSig = ""; // signature of the currently subscribed address set
  // The one meter slot is lent to another screen. Every render() ends in
  // startMeters(), and renders happen for reasons that have nothing to do with
  // this view being looked at — a device-follow reconcile re-renders the console
  // behind a modal — so without this the borrower's stream is taken back out from
  // under it, silently, and its display just stops.
  private metersLent = false;
  private subPending = false; // a registration is in flight (see startMeters)
  private raf = 0;
  private live = false;
  private visible = false;
  private meterTap = new Map<string, string>(); // node id → chosen tap key (override)
  private idsCache = { key: "", ids: new Set<string>() }; // visibleIds memo (model + hidden)
  private tapModel = ""; // model id the meterTap map was loaded for
  private railInk = new Map<string, { color: string; shadow: string }>(); // rail token → ink, per render
  private tapOpenFor: string | null = null; // node whose tap popover is open
  private readonly TAP_STORE = "urx-metertap";
  private readonly SENDS_STORE = "urx-sends-open";
  // SENDS rack global collapse (one state for every strip so the columns stay
  // aligned), persisted across sessions; the SEND PAN popover and the strip it is
  // open for. Collapse toggles a host class — no re-render — so the state is read
  // once at build and kept here. Only a stored `false` reads as collapsed; anything
  // else stored there reads as the default, open.
  private sendsOpen = loadJson<unknown>(this.SENDS_STORE, true) !== false;
  private sendPanPop!: HTMLElement;
  private sendPanOpenFor: string | null = null;
  private sendPanLocks = ""; // sendPanLockKey of the open SEND PAN popover, as it was drawn
  private tapBtn: HTMLElement | null = null; // the meter-point badge the open popover anchors to
  private sendPanBtn: HTMLElement | null = null; // the PAN ▾ button the open popover anchors to
  private tapPop!: HTMLElement;
  // The INS FX type popover and the strip it is open for, plus the chip it is anchored
  // to (either half of the pair opens it, so the anchor is not derivable from the id).
  /** The type popover: ONE element and one open-state for both selectors that use it —
   *  a strip's INS FX effect and an FX channel's EFFECT TYPE. Only one can be open, and
   *  `typePopKind` is what the reopen-after-render, the outside press and the focus
   *  restore dispatch on, since the two hang off differently classed chips. */
  private typePop!: HTMLElement;
  private typePopFor: string | null = null;
  private typePopBtn: HTMLElement | null = null;
  private typePopKind: "insfx" | "fx" = "insfx";
  private stripsHost!: HTMLElement;
  // A pointer is down inside an open popover (see `closeOnFocusLeave`).
  private pressInPop = false;
  /** The plan the strips were last built from. A different one at a render is that plan replaced. */
  private builtFor: Plan | null = null;

  constructor(
    private host: HTMLElement,
    private hooks: ConsoleHooks,
  ) {
    this.build();
    // Dev-only handle for the meter bench (scripts/meter-bench.mjs): it drives this
    // view's own paint loop from a synthetic reading feed, so the meters' render cost
    // is measurable without a device and comparable across revisions. The branch is
    // statically dropped from a production build.
    if (import.meta.env.DEV) (window as unknown as { __urxConsole?: Console }).__urxConsole = this;
  }

  // ---- public API ----

  show(): void {
    this.visible = true;
    this.host.hidden = false;
    this.render(); // render() (re)starts the meter stream when live
  }

  hide(): void {
    this.visible = false;
    this.host.hidden = true;
    // Keep the broker meter subscription alive across a view switch: re-registering
    // every meter address on each toggle stalls the readings for ~1 s. Just stop the
    // paint loop and leave the stream warm — it is torn down only when Live sync ends
    // (setLive(false) / stopMeters), so re-showing resumes from fresh data at once.
    this.stopPaint();
  }

  /** Hand the broker's meter subscription to another screen. There is one slot
   *  process-wide — a subscribe replaces the previous registration and the
   *  unsubscribe takes no address — and the replacement is silent, so a screen
   *  that wants its own addresses has to say so rather than let this view keep
   *  believing it still has a stream. */
  releaseMeters(): void {
    this.metersLent = true;
    this.stopMeters();
  }

  /** Take the slot back. A no-op unless this view is live and on screen: opened
   *  from the GRAPH inspector, the console may be hidden, and its stream is then
   *  re-established by the render() that the next show() runs. */
  regainMeters(): void {
    this.metersLent = false;
    this.startMeters();
  }

  /** Live sync turned on/off: gate the signal meter lanes and their stream. */
  setLive(active: boolean): void {
    this.live = active;
    this.host.classList.toggle("live", active);
    // The stream is bound to Live sync, not visibility (a view toggle only pauses
    // painting — see hide()); fully tear it down when live ends, or when it turns on
    // while hidden (nothing to stream until the first show re-subscribes).
    if (!active || !this.visible) this.stopMeters();
    // Rebuild so the CH → FX send Pre/Post chip flips read-only with live state;
    // render() (re)starts/re-scopes the meter stream at its tail when live.
    if (this.visible) this.render();
  }

  /** Re-read set levels after an external edit (inspector / graph / readback). */
  refresh(): void {
    if (this.visible) this.render();
  }

  /** Rebuild just one strip in place after a device-follow direct change, instead
   *  of re-rendering the whole console. No-op when hidden or when the node has no
   *  strip in the current view (mode-filtered). The strip's live-meter ballistics and
   *  keyboard focus carry across, and the meter is redrawn in the same task, so
   *  neither the meter (nor its peak-hold bar or readout) blinks nor the operator's
   *  focused control is dropped; the meter subscription is untouched (same tap). */
  refreshStrip(id: string): void {
    if (!this.visible) return;
    // An input node has no strip either: its input stage shows on every channel that reads
    // it, so each of those strips is the one to repaint.
    const model = this.hooks.getModel();
    if (model.nodes.some((n) => n.id === id && n.kind === "input")) {
      const readers = new Set(inputStageReaders(model, this.hooks.getPlan(), id).map((r) => r.channel));
      for (const ch of readers) this.refreshStrip(ch);
      return;
    }
    // A hung node (a ducker) has no strip of its own — its chip lives on the
    // parent strip (attachTo). Retarget to the parent so an external edit of the
    // child (a MIDI DUCKER toggle / device follow) repaints that chip; without
    // this the refs lookup misses and the chip stays stale until a full re-render.
    const stripId = this.hooks.getModel().nodes.find((n) => n.id === id)?.attachTo ?? id;
    const old = this.refs.get(stripId);
    if (!old) return;
    // Mark focus while the strip being replaced is still the one in refs.
    const restoreFocus = this.captureFocus();
    this.ifxCensus = insertFxCensus(this.hooks.getModel(), this.hooks.getPlan());
    const fresh = this.buildStrip(this.toStripModel(stripId));
    // buildStrip re-registered refs.get(stripId) with fresh meter elements; carry the
    // ballistics onto them so the meter (and its peak-hold bar) doesn't jump.
    this.carryMeterState(old, this.refs.get(stripId));
    old.root.replaceWith(fresh);
    this.redrawMeters(stripId);
    // The meter-point popover stays open across the rebuild — its rows do not depend on
    // the plan — so only its anchor moves to the fresh strip's badge, which the close then
    // marks shut.
    if (this.tapOpenFor === stripId) this.tapBtn = fresh.querySelector<HTMLElement>(".con-tap");
    // The SEND PAN popover floats free of its strip, so the rebuild above left an
    // open one anchored to the detached PAN button with stale knob values. Re-open
    // it against the fresh strip's button: openSendPan re-reads the plan for the
    // knobs, re-marks the live trigger (.open / aria-expanded) and re-anchors in
    // place (the fresh strip occupies the old one's slot).
    if (this.sendPanOpenFor === stripId) {
      const btn = fresh.querySelector<HTMLElement>(".con-panbtn");
      if (btn) this.openSendPan(stripId, btn);
      else this.closeSendPan();
    }
    // A MIX bus's own locks decide whether another strip's open SEND PAN knob for that bus
    // is live, so a rebuilt MIX strip re-opens the popover against its owner's button — or
    // closes it where that button is gone — when one of those locks changed. Only then: a
    // re-open under a knob being dragged ends the drag.
    const panOwner = this.sendPanOpenFor;
    if (
      panOwner !== null &&
      panOwner !== stripId &&
      this.isMixBus(stripId) &&
      this.sendPanLockKey(panOwner) !== this.sendPanLocks
    ) {
      const btn = this.refs.get(panOwner)?.root.querySelector<HTMLElement>(".con-panbtn");
      if (btn) this.openSendPan(panOwner, btn);
      else this.closeSendPan();
    }
    // Same for the INS FX popover, and for a second reason: its list is drawn from the
    // plan the strip was just rebuilt from, so a device-side selection would otherwise
    // leave the open list naming what the strip no longer holds. Re-opening re-reads it.
    if (this.typePopFor === stripId) {
      const fx = this.typePopKind === "fx";
      const chip = fresh.querySelector<HTMLElement>(fx ? ".con-fxopen" : ".con-ifxopen");
      if (!chip) this.closeTypePop();
      else if (fx) this.openFxTypePop(stripId, chip);
      else this.openInsFxPop(stripId, chip);
    }
    restoreFocus();
  }

  // ---- build / render ----

  private build(): void {
    this.host.classList.add("con-root");
    this.host.classList.toggle("sends-collapsed", !this.sendsOpen);

    this.stripsHost = el("div", "con-strips");
    const wrap = el("div", "con-wrap");
    wrap.append(this.stripsHost);
    this.host.append(wrap);

    // Floating meter-point popover (positioned fixed so it escapes the strip
    // scroll container). One element reused for whichever strip opened it.
    this.tapPop = el("div", "con-tappop");
    this.tapPop.hidden = true;
    this.host.append(this.tapPop);
    // SEND PAN popover: one reused element, anchored below the strip's PAN ▾ button
    // (the .below/.above caret class is set per open by placePopover's flip result).
    this.sendPanPop = el("div", "con-spop");
    this.sendPanPop.hidden = true;
    this.host.append(this.sendPanPop);
    // INS FX type popover: same arrangement, anchored to whichever half of the strip's
    // INS FX pair was pressed.
    this.typePop = el("div", "con-ifxpop");
    this.typePop.hidden = true;
    this.host.append(this.typePop);
    // Close any popover on an outside interaction (each trigger manages its own toggle,
    // so a press on the trigger is excluded).
    document.addEventListener("pointerdown", (e) => {
      const tgt = e.target as HTMLElement;
      this.pressInPop = this.popovers.some((p) => p.openFor !== null && p.box.contains(tgt));
      if (this.tapOpenFor && !this.tapPop.contains(tgt) && !tgt.closest(".con-tap")) this.closeTapPop();
      if (this.sendPanOpenFor && !this.sendPanPop.contains(tgt) && !tgt.closest(".con-panbtn")) this.closeSendPan();
      if (this.typePopFor && !this.typePop.contains(tgt) && !tgt.closest(".con-ifxface, .con-ifxopen, .con-fxopen"))
        this.closeTypePop();
    });
    // Escape is a KEYBOARD dismissal, so the focus goes back to what opened the popover:
    // the row that had it is about to be destroyed, and without this the operator lands on
    // <body> and has to tab in from the top of the document. An outside PRESS asks for no
    // restore — the focus belongs to whatever was pressed, and taking it back would move
    // the caret out of the control the operator just aimed at.
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      if (this.sendPanOpenFor) this.closeSendPan(true);
      if (this.tapOpenFor) this.closeTapPop(true);
      if (this.typePopFor) this.closeTypePop(true);
    });
    const pressEnd = (): void => void (this.pressInPop = false);
    document.addEventListener("pointerup", pressEnd);
    document.addEventListener("pointercancel", pressEnd);
    this.host.addEventListener("focusout", (e) => this.closeOnFocusLeave(e));
    this.tapPop.addEventListener("keydown", (e) => this.tabOutOfPopover(e, "tap"));
    this.sendPanPop.addEventListener("keydown", (e) => this.tabOutOfPopover(e, "pan"));
    this.typePop.addEventListener("keydown", (e) => this.tabOutOfPopover(e, "ifx"));
  }

  /**
   * Leave a popover by Tab at its place in the tab order, which is right after the control
   * that opened it, rather than at the end of the document where it is appended. Shift+Tab on
   * its first control closes it and lands on that trigger; Tab on its last control closes it
   * and lands on the first control after the trigger that takes the focus. Tab between its
   * own controls is left to the browser.
   */
  private tabOutOfPopover(e: KeyboardEvent, kind: "tap" | "pan" | "ifx"): void {
    if (e.key !== "Tab" || isChord(e)) return;
    const p = this.popovers.find((x) => x.kind === kind);
    if (!p || p.openFor === null) return;
    const rows = focusables(p.box).filter(tabbable);
    const at = rows.indexOf(document.activeElement as HTMLElement);
    if (at < 0 || at !== (e.shiftKey ? 0 : rows.length - 1)) return;
    e.preventDefault();
    if (kind === "tap") this.closeTapPop(true);
    else if (kind === "pan") this.closeSendPan(true);
    else this.closeTypePop(true);
    const trigger = document.activeElement;
    if (e.shiftKey || !(trigger instanceof HTMLElement) || !this.host.contains(trigger)) return;
    const all = focusables(document.body).filter(tabbable);
    for (const next of all.slice(all.indexOf(trigger) + 1)) {
      next.focus();
      if (document.activeElement === next) return;
    }
  }

  /**
   * Close a popover once the keyboard focus leaves both it and the trigger that opened it —
   * a Tab out of its last row, a Shift+Tab out of its first — so an open popover does not
   * stand over the strips the focus has moved on to. Closed without handing the focus back:
   * it has already gone somewhere the operator chose.
   *
   * A focusout that names no new target is not the focus leaving in two cases, and is
   * ignored in both: the window losing the OS foreground (WKWebView blurs the focused element
   * then, and `document.hasFocus()` answers false), and a press on a part of the popover that
   * takes no focus (its header, its foot). A focused element this view's own rebuild removes
   * (Chromium reports that as a focusout naming no target) closes nothing either: it belongs
   * to no strip on screen by then, and a popover being re-opened has let go of its open state.
   */
  private closeOnFocusLeave(e: FocusEvent): void {
    const to = e.relatedTarget instanceof Node ? e.relatedTarget : null;
    if (to === null && (!document.hasFocus() || this.pressInPop)) return;
    const from = e.target instanceof Node ? e.target : null;
    if (from === null) return;
    for (const p of this.popovers) {
      if (p.openFor === null) continue;
      const root = this.refs.get(p.openFor)?.root;
      const owns = (n: Node): boolean =>
        p.box.contains(n) ||
        (n instanceof Element && !!root?.contains(n) && p.sel.some((sel) => n.closest(sel) !== null));
      if (!owns(from) || (to !== null && owns(to))) continue;
      if (p.kind === "tap") this.closeTapPop();
      else if (p.kind === "pan") this.closeSendPan();
      else this.closeTypePop();
    }
  }

  // dB tick labels for a strip's fader range (per-channel scale between the fader
  // and meter). Top/bottom align with the fader travel, so it reads the level. Ticks
  // above `ceilingDb` are dropped — strips whose fader/meter top out at 0 dB (OSC,
  // the meter-only STREAMING strip) don't label the unreachable +5/+10 marks.
  private buildScale(range: LevelRange, ceilingDb = range.max): HTMLElement {
    const scale = el("div", "con-scale");
    for (const db of range.ticks) {
      if (db > ceilingDb) continue;
      const tick = el("div", "t");
      // The bottom tick reads -∞ (off), so it sits at the fader's off position —
      // the very bottom of the travel — not at the lowest detent one notch above.
      const isOff = db <= range.min;
      tick.style.bottom = dbToFrac(isOff ? range.off : db, range) * 100 + "%";
      // The number is centred; a minus sign hangs to its left so the digits of
      // e.g. "10" and "-10" line up vertically.
      const num = el("span", "num");
      if (db < 0) {
        const sign = el("span", "sign");
        sign.textContent = "−";
        num.append(sign);
      }
      if (isOff) {
        const inf = el("span", "glyph-inf");
        inf.textContent = "∞";
        num.append(inf);
      } else {
        num.append(document.createTextNode(String(Math.abs(db))));
      }
      tick.append(num);
      scale.append(tick);
    }
    return scale;
  }

  // The node ids on screen: every model node minus the ones shelved out of the
  // graph (a shelved node drops from the console too). Shared by the strip groups,
  // the rack send slots and the head-height probe so "visible" is defined once.
  // Memoized on model + hidden set, since a single render resolves it once per strip
  // (and once per rack) — all with the same answer.
  private visibleIds(): Set<string> {
    const hidden = this.hooks.getPlan().hidden;
    const key = this.hooks.getModel().id + "|" + hidden.join(",");
    if (this.idsCache.key !== key) {
      const h = new Set(hidden);
      this.idsCache = {
        key,
        ids: new Set(
          this.hooks
            .getModel()
            .nodes.map((n) => n.id)
            .filter((i) => !h.has(i)),
        ),
      };
    }
    return this.idsCache.ids;
  }

  private stripModels(): { groups: { label: string; ids: string[] }[]; master: string | null } {
    const model = this.hooks.getModel();
    const ids = this.visibleIds();
    const channels = model.nodes.filter((n) => n.kind === "channel" && ids.has(n.id)).map((n) => n.id);
    const busFx = ["bus.fx1", "bus.fx2", "bus.mix1", "bus.mix2", "bus.stream"].filter((i) => ids.has(i));
    const mon = ["bus.mon1", "bus.mon2", "bus.osc"].filter((i) => ids.has(i));
    const groups = [
      { label: t().console.groupInputs, ids: channels },
      { label: t().console.groupBus, ids: busFx },
      { label: t().console.groupMon, ids: mon },
    ].filter((g) => g.ids.length > 0);
    return { groups, master: ids.has("bus.stereo") ? "bus.stereo" : null };
  }

  private toStripModel(id: string): StripModel {
    const node = this.hooks.getModel().nodes.find((n) => n.id === id)!;
    const isChannel = node.kind === "channel";
    const isMaster = id === MAIN_BUS;
    const isOsc = id === "bus.osc";
    const isStream = id === "bus.stream";
    const isMix = this.isMixBus(id);
    const isMon = isMonitorBus(id);
    const isMono = /^ch\d+$/.test(id); // mono channels are ch1..ch4 (the only gain/gate/comp/Ф-bearing strips)
    return {
      id,
      // The master reads "STEREO" here (the graph keeps the fuller "STEREO (MAIN)"):
      // the strip is narrow, and the LED + name must fit one line.
      label: isMaster ? "STEREO" : node.label,
      // Monitors carry no device CH SETTING name; their second row instead names
      // the linked PHONES output (PHONES 1 ↔ mon1, PHONES 2 ↔ mon2).
      deviceName: isMon ? `Phone ${id.slice(-1)}` : this.hooks.getPlan().nodeNames[id] || "",
      rail: `var(--rail-${node.kind})`,
      isChannel,
      isMono,
      // Mono channels read PAN unless STEREO-linked in BAL mode; native stereo / FX
      // channels always read BALANCE — matching the inspector (isBalanceChannel).
      isBalance: !isMono || isBalLinkedPair(this.hooks.getModel(), this.hooks.getPlan(), id),
      fadersOnly: !(isChannel || this.isFxChannel(id)),
      isOsc,
      isStream,
      // The MUTE chip exists only on strips that send to STEREO (CH / FX → STEREO
      // assign, MIX → STEREO "TO ST"): it toggles that send. STEREO / MONITOR have
      // no such send, so their master ON is the scribble power LED alone.
      hasMute: isChannel || this.isFxChannel(id) || isMix,
      hasEq: isChannel || isMix || isMaster,
      hasPhones: isMonitorBus(id),
      // Off-state dim, computed once here (the node is in hand) and read by both strip
      // builders — the same predicate the graph uses, so the two views dim alike.
      inactive: isNodeInactive(this.hooks.getPlan(), node),
      meterOnly: isStream || isOsc, // STREAMING + OSC: no fader (OSC uses a level knob)
      // OSC drives its level via the LEVEL knob, so its meter/scale use the shared
      // level_gain ruler like every other strip (and the meter-only STREAMING strip).
      range: NORMAL_RANGE,
    };
  }

  // The MIX/FX send targets that exist in this model and are not shelved out of the
  // graph — the fixed column set for every strip's SENDS rack (a shelved bus drops
  // its column on every strip). Order follows SEND_TARGETS: FX 1, FX 2, MIX 1, MIX 2.
  private sendSlots(): SendTarget[] {
    const ids = this.visibleIds();
    return SEND_TARGETS.filter((s) => ids.has(s));
  }

  // ---- meter point (per-strip tap selection) ----

  /** The tap key a strip's meter shows: the per-strip override or the default. */
  private tapKeyOf(id: string): string {
    return this.meterTap.get(id) ?? defaultTapKey(id, this.hooks.getModel().id);
  }

  // Persist the per-strip tap choices per model in localStorage (shape:
  // { [modelId]: { [nodeId]: tapKey } }), reusing the shared JSON storage helpers. A
  // stored value that is not an object (null, a string, a number, an array) reads as
  // no choices at all, so a pick writes a fresh container instead of throwing.
  private allTaps(): Record<string, unknown> {
    const all = loadJson<unknown>(this.TAP_STORE, {});
    return isPlainRecord(all) ? all : {};
  }

  private loadTaps(): void {
    this.meterTap.clear();
    const m = this.allTaps()[this.hooks.getModel().id];
    if (isPlainRecord(m)) for (const [k, v] of Object.entries(m)) if (typeof v === "string") this.meterTap.set(k, v);
  }

  private saveTaps(): void {
    const all = this.allTaps();
    all[this.hooks.getModel().id] = Object.fromEntries(this.meterTap);
    saveJson(this.TAP_STORE, all);
  }

  /** Apply a per-strip tap choice, persist it, and rebuild (re-scopes the stream). */
  private setTap(id: string, key: string): void {
    this.meterTap.set(id, key);
    this.saveTaps();
    // The close is left to `render`, which takes the focus decision before it destroys
    // anything: choosing a tap from the KEYBOARD leaves the focus on the row this would
    // otherwise remove first, and a capture taken after that has nothing to record.
    this.render();
  }

  // Build a strip's meter-point badge (the popover trigger). Shown only when the
  // node has more than one tap; single-meter nodes get no selector.
  private buildTapBadge(id: string): HTMLElement {
    const tap = tapFor(id, this.tapKeyOf(id), this.hooks.getModel().id);
    const badge = el("div", "con-tap");
    badge.dataset.ctl = "meter-point";
    badge.setAttribute("role", "button");
    badge.setAttribute("aria-haspopup", "menu");
    // A badge rebuilt under its own open popover (`refreshStrip`) reads open from the start.
    badge.setAttribute("aria-expanded", String(this.tapOpenFor === id));
    badge.tabIndex = 0;
    // A small meter-bars glyph marks this as the METER point selector — so it
    // reads apart from the send-tap PRE/POST chip (which shares the pre/post
    // vocabulary but controls the send, not the meter).
    const ico = meterGlyph();
    const name = document.createTextNode(tap?.label ?? "");
    const cv = el("span", "cv");
    cv.textContent = "▾";
    badge.append(ico, name, cv);
    const toggle = (viaKey: boolean): void => {
      if (this.tapOpenFor === id) this.closeTapPop();
      else this.openTapPop(id, badge, viaKey);
    };
    badge.addEventListener("click", () => toggle(false));
    badge.addEventListener("keydown", (e) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        toggle(true);
      } else if (e.key === "Escape") {
        this.closeTapPop();
      }
    });
    return badge;
  }

  // Open the floating meter-point popover for a node, anchored to its badge. The
  // chain lists the node's taps in signal order with the active one highlighted.
  /** The single-popover invariant, in ONE place. Each opener used to close the others by
   *  hand, and the list went out of date the day a third popover arrived: INS FX was added
   *  to its own opener and to neither of the other two, so reaching the meter point or SEND
   *  PAN from the KEYBOARD — where no `pointerdown` fires the document handler that would
   *  otherwise have covered it — left two popovers on screen. Closing all three includes the
   *  one about to open, which every opener rebuilds anyway. */
  /**
   * Hand the focus back to what opened a popover, on a KEYBOARD dismissal only.
   *
   * The row that had the focus is destroyed by the close, so without this the operator is
   * left on <body> and tabs in from the top of the document. It is deliberately not done
   * on an outside press: the focus belongs to whatever was pressed, and taking it back
   * would move the caret out of the control the operator aimed at. A trigger that is no
   * longer in the document — a re-render replaced it — is left alone here; the caller that
   * caused the re-render is the one that knows where the strip went (`focusInsFxAnchor`).
   */
  private releaseFocus(trigger: HTMLElement | null, restore: boolean, id: string | null, sel: string): void {
    if (!restore) return;
    if (trigger?.isConnected) {
      trigger.focus();
      return;
    }
    // The trigger this popover was opened from is DETACHED: a repaint replaced the strip
    // while the popover stood open, which a device-follow does on its own. The popovers
    // live outside the strip rack and are not rebuilt with it, so the focus inside one
    // survives — but the element to hand it back to does not, and without this Escape
    // after such a repaint drops to <body> exactly as it did before. Resolved from the
    // strip that is there now, which is the same move the focus carry-over makes.
    const root = id === null ? undefined : this.refs.get(id)?.root;
    root?.querySelector<HTMLElement>(sel)?.focus();
  }

  private closePopovers(): void {
    this.closeTapPop();
    this.closeSendPan();
    this.closeTypePop();
  }

  private openTapPop(id: string, anchor: HTMLElement, focusIn = false): void {
    this.closePopovers();
    const cur = this.tapKeyOf(id);
    this.tapPop.replaceChildren();
    const ph = el("div", "ph");
    ph.textContent = t().console.meterPoint;
    const chain = el("div", "chain");
    chain.setAttribute("role", "menu");
    chain.setAttribute("aria-label", t().console.meterPoint);
    this.wireRowKeys(chain);
    for (const tp of tapsFor(id, this.hooks.getModel().id)) {
      const row = el("div", "crow" + (tp.key === cur ? " active" : ""));
      row.dataset.ctl = "tap:" + tp.key;
      row.setAttribute("role", "menuitemradio");
      row.setAttribute("aria-checked", String(tp.key === cur));
      row.tabIndex = 0;
      const node = el("span", "node");
      const nm = el("span", "nm");
      nm.textContent = tp.label;
      row.append(node, nm);
      const pick = (): void => this.setTap(id, tp.key);
      row.addEventListener("click", pick);
      row.addEventListener("keydown", (e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          pick();
        }
      });
      chain.append(row);
    }
    const foot = el("div", "foot");
    foot.textContent = t().console.meterPointHint;
    this.tapPop.append(ph, chain, foot);
    this.tapPop.hidden = false;
    this.tapOpenFor = id;
    this.tapBtn = anchor;
    anchor.setAttribute("aria-expanded", "true");
    // Position fixed near the badge, clamped to the viewport (top-right aligned).
    this.placePopover(this.tapPop, anchor, "right", 2);
    if (focusIn) this.focusInto(this.tapPop);
  }

  /**
   * Move the focus into a popover the KEYBOARD opened: onto its checked row, or its first
   * control where no row is checked or the checked one takes no focus. A popover is appended
   * after the whole strip rack, so without this the next Tab walks the rest of the rack
   * before it reaches the popover. Nothing moves where the popover offers the keyboard no
   * control. A pointer open and the re-open after a strip rebuild leave the focus alone.
   */
  private focusInto(pop: HTMLElement): void {
    const rows = focusables(pop);
    const checked = rows.find((r) => r.getAttribute("aria-checked") === "true");
    (checked ?? rows[0])?.focus({ preventScroll: true });
  }

  /**
   * The keys a popover's list of rows answers, the ones the toolbar menus answer: Down / Up move
   * the focus to the next / previous row that takes it, wrapping at the ends, and Home / End to
   * the first / last. A row that cannot be picked takes no focus, so it is passed over. Tab still
   * walks the rows and leaves the list; a key held with a command modifier is left alone.
   * `ends` false answers Down / Up alone and leaves Home / End to the focused control — the SEND
   * PAN knobs, walked by Down / Up while Left / Right step the focused one's value.
   */
  private wireRowKeys(list: HTMLElement, ends = true): void {
    list.addEventListener("keydown", (e) => {
      if (isChord(e)) return;
      const rows = focusables(list);
      const at = rows.indexOf(document.activeElement as HTMLElement);
      if (at < 0) return;
      const to =
        e.key === "ArrowDown"
          ? (at + 1) % rows.length
          : e.key === "ArrowUp"
            ? (at - 1 + rows.length) % rows.length
            : ends && e.key === "Home"
              ? 0
              : ends && e.key === "End"
                ? rows.length - 1
                : null;
      if (to === null) return;
      e.preventDefault();
      rows[to].focus();
    });
  }

  private closeTapPop(restore = false): void {
    if (!this.tapOpenFor) return;
    const openFor = this.tapOpenFor;
    this.tapOpenFor = null;
    this.tapPop.hidden = true;
    this.tapPop.replaceChildren();
    this.tapBtn?.setAttribute("aria-expanded", "false");
    this.releaseFocus(this.tapBtn, restore, openFor, ".con-tap");
    this.tapBtn = null;
  }

  // ---- SENDS rack ----

  // Build the per-strip SENDS rack (between the head and the fader zone): a header
  // (SENDS label / value readout / global collapse arrow + collapsed active-send
  // dots) and, for a strip that has sends, one fixed column per model send slot
  // (enable chip → PRE button → vertical mini-fader) plus a full-width PAN ▾ button
  // opening the SEND PAN popover. A strip with no sends renders the dimmed header
  // only (its arrow still drives the global collapse); a slot the strip lacks leaves
  // an empty column so columns stay aligned across strips.
  private buildSendRack(m: StripModel): { el: HTMLElement; cols: SendColRef[] } {
    const slots = this.sendSlots();
    const owned = slots.map((s) => this.hasSend(m.id, s)); // hasSend excludes self-sends
    const hasAny = owned.some(Boolean);
    const rack = el("div", "con-sends" + (hasAny ? "" : " empty"));

    const sh = el("div", "con-sh" + (hasAny ? "" : " dim"));
    sh.dataset.ctl = "sends";
    sh.setAttribute("role", "button");
    sh.setAttribute("aria-expanded", String(this.sendsOpen));
    sh.tabIndex = 0;
    const lb = el("span", "lb");
    lb.textContent = t().console.sends;
    const rdout = el("span", "rdout");
    const ar = el("span", "ar");
    const dots = el("span", "dots");
    sh.append(lb, rdout, dots, ar);
    const toggle = (): void => this.toggleSends();
    sh.addEventListener("click", toggle);
    sh.addEventListener("keydown", (e) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        toggle();
      }
    });
    // Hovering one header previews the global scope: highlight every header at once.
    sh.addEventListener("pointerenter", () => this.host.classList.add("sends-hover"));
    sh.addEventListener("pointerleave", () => this.host.classList.remove("sends-hover"));
    rack.append(sh);
    if (!hasAny) return { el: rack, cols: [] };

    // The header label swaps to a value readout while a column is touched.
    const swap = (text: string | null): void => {
      if (text === null) sh.classList.remove("readout");
      else {
        rdout.textContent = text;
        sh.classList.add("readout");
      }
    };

    const cols: SendColRef[] = [];
    const scols = el("div", "con-scols");
    slots.forEach((s, i) => {
      if (!owned[i]) {
        scols.append(el("div", "con-scol empty"));
        return;
      }
      const built = this.buildSendCol(m, s, swap);
      cols.push(built.ref);
      scols.append(built.el);
    });
    rack.append(scols);
    this.fillDots(dots, m.id, slots);

    // PAN ▾ button → SEND PAN popover (send-pan is a MIX-send subparameter with no
    // room on the narrow column, so it lives in a popover below this button).
    const panbtn = el("button", "con-panbtn") as HTMLButtonElement;
    panbtn.type = "button";
    panbtn.dataset.strip = m.id;
    panbtn.dataset.ctl = "send-pan";
    panbtn.setAttribute("aria-haspopup", "true");
    panbtn.setAttribute("aria-expanded", "false");
    const cv = el("span", "cv");
    cv.textContent = "▾";
    panbtn.append(document.createTextNode("PAN"), cv);
    // A native button: a click the keyboard produced (Enter / Space) carries no click count.
    panbtn.addEventListener("click", (e) => {
      if (this.sendPanOpenFor === m.id) this.closeSendPan();
      else this.openSendPan(m.id, panbtn, e.detail === 0);
    });
    rack.append(panbtn);
    return { el: rack, cols };
  }

  // One send column: enable chip (params.on, amber = active) → PRE button (params.tap,
  // amber = pre; read-only for a CH → FX tap while live) → vertical mini-fader
  // (params.level, relative drag, snapped to the level_gain grid).
  private buildSendCol(
    m: StripModel,
    target: SendTarget,
    swap: (text: string | null) => void,
  ): { el: HTMLElement; ref: SendColRef } {
    const range = m.range;
    // The column's connection object is stable for this build's lifetime — edits
    // mutate its `params` in place, and a plan swap re-renders — so capture it once
    // instead of re-scanning plan.connections for every read/write. `pre`/`level`/`on`
    // are read off `c.params` live (reassigned in place).
    const c = sendConnection(this.hooks.getPlan(), m.id, target);
    const isMix = this.isMixBus(target);
    // The bus this column aims at is gone at this rate, so the whole column is: its
    // switch, its tap and its level all set something on a bus the unit is not running.
    // FIXED is the narrower lock below — that one takes the level and the tap and leaves
    // the switch.
    const rateOff =
      nodeRateDisabled(target, this.hooks.getPlan().sampleRate) ||
      nodeRateDisabled(m.id, this.hooks.getPlan().sampleRate);
    // FIXED BUS Type leaves a MIX send its ON alone: the level and the PRE tap turn
    // read-only (matching the graph inspector, which drops them) and the enable chip
    // stays editable.
    const mixFixed = isMix && mixSendLocks(this.hooks.getPlan(), target).busFixed;
    const busFixed = rateOff || mixFixed;

    const col = el("div", "con-scol" + (c?.params?.on !== false ? "" : " off") + (rateOff ? " rate-off" : ""));
    // vertical mini-fader (built first so the PRE button can refresh its aria-valuetext)
    const fader = el("div", "con-vfad" + (busFixed ? " readonly" : ""));
    fader.setAttribute("role", "slider");
    fader.setAttribute("aria-label", SEND_LABEL[target]);
    fader.dataset.ctl = controlId(m.id, "level", target);
    setLevelRange(fader, range);
    if (busFixed) {
      fader.setAttribute("aria-disabled", "true");
      setTip(fader, rateOff ? t().inspector.fx2RateLocked : t().inspector.busFixedSend);
    } else {
      fader.tabIndex = 0;
    }
    const cap = el("div", "cap");
    const zero = el("div", "zero");
    zero.style.setProperty("--zero", (1 - dbToFrac(0, range)) * 100 + "%");
    fader.append(el("div", "track"), zero, cap);
    const ref: SendColRef = { target, fader, cap };
    // Destination and level only. The tap is already on screen — this column's own PRE
    // button, lit while pre-fader — and naming it here as well takes the longest readings
    // past the header's share of the strip, which clips the value the readout exists to
    // show. The button's `aria-pressed` is the tap's accessible form; the fader's
    // `aria-valuetext` says "PRE, " in front of a level above the floor and nothing in
    // front of a post-fader one.
    const readoutText = (): string => SEND_LABEL[target] + " " + fmtDb(c?.params?.level ?? LEVEL_OFF_DB, range).text;

    // enable chip
    const chip = this.buildChip(
      m.id,
      SEND_SHORT[target],
      c?.params?.on !== false,
      () => {
        const next = c?.params?.on === false; // was off → turn on
        if (c) c.params = { ...c.params, on: next };
        return next;
      },
      rateOff
        ? { cls: "con-sl", readonlyTitle: t().inspector.fx2RateLocked, midiId: controlId(m.id, "mute", target) }
        : {
            cls: "con-sl",
            midiId: controlId(m.id, "mute", target),
            after: (next) => col.classList.toggle("off", !next),
          },
    );

    // PRE button
    // Port refs, not bare node ids: the routing rules are keyed by `node:port`, so a bare
    // id matches no rule and `sendTapWritable` answers false for every send — which while
    // live locked every PRE chip (and dropped its MIDI binding), including the CH → MIX
    // and FX-channel → MIX taps the device does accept.
    const tapReadonly =
      this.live && !sendTapWritable(this.hooks.getModel(), portRef(m.id, "out"), portRef(target, "in"));
    const preBtn = this.buildChip(
      m.id,
      t().console.pre,
      c?.params?.tap === "pre",
      () => {
        const next = c?.params?.tap !== "pre";
        if (c) c.params = { ...c.params, tap: next ? "pre" : "post" };
        this.updateColLevel(ref, range, c?.params?.level ?? LEVEL_OFF_DB, next); // aria-valuetext's PRE prefix
        return next;
      },
      rateOff
        ? {
            cls: "con-slp",
            readonlyTitle: t().inspector.fx2RateLocked,
            midiId: isMix ? controlId(m.id, "tap", target) : undefined,
          }
        : mixFixed
          ? { cls: "con-slp", readonlyTitle: t().inspector.busFixedSend }
          : tapReadonly
            ? { cls: "con-slp", readonlyTitle: t().inspector.prePostLcdOnly }
            : {
                cls: "con-slp",
                midiId: isMix ? controlId(m.id, "tap", target) : undefined,
                title: t().console.preHint,
              },
    );

    // Each keeps one identity whatever lock it is drawn under, for the focus carry-over.
    chip.dataset.ctl = controlId(m.id, "mute", target);
    preBtn.dataset.ctl = controlId(m.id, "tap", target);

    // A FIXED-bus send fader is display-only: paint its value but skip the wiring.
    if (!busFixed) this.wireColFader(m.id, target, c, ref, range, swap, readoutText);
    else if (rateOff) this.learnOnly(fader, controlId(m.id, "level", target));
    this.updateColLevel(ref, range, c?.params?.level ?? LEVEL_OFF_DB, c?.params?.tap === "pre");
    col.append(chip, preBtn, fader);
    return { el: col, ref };
  }

  // Wire a send column's vertical mini-fader: relative drag (no jump-to-click, since
  // one pixel is a whole detent), a 3 px threshold before the first write, Shift =
  // fine, and the keyboard grid steps of the main fader. The header readout mirrors
  // the value while the column is touched, then reverts to the SENDS label.
  private wireColFader(
    node: string,
    target: SendTarget,
    c: PlanConnection | undefined,
    ref: SendColRef,
    range: LevelRange,
    swap: (text: string | null) => void,
    readoutText: () => string,
  ): void {
    const { fader } = ref;
    const midiId = controlId(node, "level", target);
    this.midiMark(fader, midiId);
    const level = (): number => c?.params?.level ?? LEVEL_OFF_DB;
    // The header readout is shared by the rack's columns, so revert it only when no
    // column in this rack is still hovered or focused (else leaving column B would
    // clear column A's readout while A keeps focus). The rack ancestor is fixed for
    // the fader's lifetime, so resolve it once.
    const rack = fader.closest(".con-sends");
    const rackTouched = (): boolean => !!rack?.querySelector(".con-vfad:hover, .con-vfad:focus");
    const set = (db: number): void => {
      if (c) c.params = { ...c.params, level: db };
      this.updateColLevel(ref, range, db, c?.params?.tap === "pre");
      swap(readoutText());
      this.commit(node);
      this.mirrorPartnerSend(node, target);
    };
    let dragging = false;
    fader.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      if (this.midiArm(midiId)) return;
      dragging = true;
      // Both anchors are rebased whenever the Shift state flips, as the head knob's are,
      // so entering or leaving fine mode mid-drag continues from the level on screen
      // instead of applying the new rate to the whole distance already dragged.
      let startY = e.clientY;
      let startFrac = dbToFrac(level(), range);
      let wasShift = e.shiftKey;
      // The cap centre travels the element's whole height (`--pos`, under a -50% translate),
      // the main fader's scale too, so a pixel dragged is the same share of the level on both.
      const travel = fader.getBoundingClientRect().height;
      let moved = false;
      trackDrag(
        fader,
        e,
        (ev) => {
          if (ev.shiftKey !== wasShift) {
            startY = ev.clientY;
            startFrac = dbToFrac(level(), range);
            wasShift = ev.shiftKey;
          }
          const dy = startY - ev.clientY;
          if (!moved && Math.abs(dy) < 3) return; // threshold guards mis-grabs / dblclick
          moved = true;
          const frac = startFrac + (dy * (ev.shiftKey ? 0.25 : 1)) / travel;
          set(fracToDb(frac, range));
        },
        {
          onEnd: () => {
            dragging = false;
            swap(rackTouched() ? readoutText() : null); // keep it if still hovered/focused
          },
        },
      );
    });
    fader.addEventListener("keydown", (e) => {
      if (this.midiLearnKey(e, midiId)) return;
      const next = this.faderKeyStep(e, range, level());
      if (next === null) return;
      e.preventDefault();
      set(next);
    });
    fader.addEventListener("pointerenter", () => swap(readoutText()));
    fader.addEventListener("pointerleave", () => {
      // Keep the readout up while dragging (pointer captured) or if any sibling column
      // in this rack is still hovered / keyboard-focused.
      if (!dragging && !rackTouched()) swap(null);
    });
    fader.addEventListener("focus", () => swap(readoutText()));
    fader.addEventListener("blur", () => {
      if (!rackTouched()) swap(null);
    });
    fader.addEventListener("dblclick", () => {
      if (this.hooks.midi?.learnActive()) return; // pointerdown already armed
      set(this.sendLevelOf(this.factoryPlan(), node, target));
    });
    // Hover + wheel steps one detent, matching the main fader. The pointer sits over
    // the column while scrolling, so pointerenter has already surfaced the readout;
    // set() keeps it in step.
    onWheelStep(
      fader,
      (dir) => set(this.faderWheelStep(range, level(), dir)),
      () => this.hooks.midi?.learnActive(),
    );
  }

  // The next fader level for a keydown (Arrow = 1 detent, PageUp/Down = 6, Home = max,
  // End = −∞), or null for a non-stepping key. Shared by the main fader and the rack
  // columns; a step down off the floor lands on −∞ via the range's own step(). A level
  // below the floor steps from −∞, so one step up from there lands on the floor detent.
  private faderKeyStep(e: KeyboardEvent, range: LevelRange, cur: number): number | null {
    const base = cur < range.min ? range.off : cur;
    if (e.key === "ArrowUp") return range.step(base, 1);
    if (e.key === "ArrowDown") return range.step(base, -1);
    if (e.key === "PageUp") return range.step(base, 6);
    if (e.key === "PageDown") return range.step(base, -6);
    if (e.key === "Home") return range.max;
    if (e.key === "End") return range.off;
    return null;
  }

  // One detent up/down from a wheel notch, mirroring the Arrow keys (a step down
  // off the floor lands on −∞ via the range's own step(), and a level below the
  // floor steps from −∞). Shared by both faders.
  private faderWheelStep(range: LevelRange, cur: number, dir: 1 | -1): number {
    const base = cur < range.min ? range.off : cur;
    return range.step(base, dir);
  }

  // Paint a send column's fader cap position + accessible value from a dB level + tap.
  private updateColLevel(ref: SendColRef, range: LevelRange, db: number, pre: boolean): void {
    ref.cap.style.setProperty("--pos", (1 - dbToFrac(db, range)) * 100 + "%");
    ref.fader.setAttribute("aria-valuenow", String(Math.round(db)));
    ref.fader.setAttribute("aria-valuetext", levelValueText(db, range, pre ? "PRE, " : ""));
  }

  // Fill a collapsed-header dots row: one amber dot per active (ON) send.
  private fillDots(dots: HTMLElement, id: string, slots: SendTarget[]): void {
    dots.replaceChildren();
    for (const s of slots) {
      if (s === id) continue;
      const c = sendConnection(this.hooks.getPlan(), id, s);
      if (c && c.params?.on !== false) dots.append(el("i", ""));
    }
  }

  // Toggle the global SENDS collapse (one state for every strip so the columns stay
  // aligned): flip the host class, persist, and in one pass per strip reset the value
  // readout, sync aria-expanded, and refresh the collapsed dots (all reached via the
  // strip's `.con-sh` header, so no separate DOM sweep is needed).
  private toggleSends(): void {
    this.sendsOpen = !this.sendsOpen;
    saveJson(this.SENDS_STORE, this.sendsOpen);
    this.host.classList.toggle("sends-collapsed", !this.sendsOpen);
    this.closeSendPan();
    const slots = this.sendSlots();
    for (const ref of this.refs.values()) {
      const sh = ref.root.querySelector<HTMLElement>(".con-sh");
      if (!sh) continue;
      sh.classList.remove("readout");
      sh.setAttribute("aria-expanded", String(this.sendsOpen));
      const dots = sh.querySelector<HTMLElement>(".dots");
      if (dots) this.fillDots(dots, ref.m.id, slots);
    }
  }

  /** The FIXED lock and the Pan Link of every MIX bus a strip's SEND PAN popover draws a
   *  knob for, as one comparable string: FIXED locks a knob, and Pan Link moves the send pan
   *  it shows to the source's own. */
  private sendPanLockKey(stripId: string): string {
    const plan = this.hooks.getPlan();
    return this.sendSlots()
      .filter((target) => this.isMixBus(target) && this.hasSend(stripId, target))
      .map((target) => {
        const { busFixed, panLinked } = mixSendLocks(plan, target);
        return `${target}:${busFixed}:${panLinked}`;
      })
      .join(",");
  }

  // Open the SEND PAN popover below a strip's PAN ▾ button: the strip's MIX sends'
  // pan as rotary knobs laid out in horizontal columns (destination label above,
  // value below), echoing the rack columns. FX sends are mono and carry no pan.
  private openSendPan(stripId: string, anchor: HTMLElement, focusIn = false): void {
    this.closePopovers();
    const plan = this.hooks.getPlan();
    this.sendPanPop.replaceChildren();
    // The popover floats free of its strip once open, so name the owning strip in
    // the header — position alone no longer ties it back.
    const ph = el("div", "ph");
    const cat = el("span", "cat");
    cat.textContent = t().console.sendPan;
    const who = el("span", "who");
    who.textContent = this.toStripModel(stripId).label;
    ph.append(cat, who);
    const grid = el("div", "pcols");
    this.wireRowKeys(grid, false);
    // A strip the rate removes holds its send pans read-only, and assignable, as its rack does.
    const selfOff = nodeRateDisabled(stripId, plan.sampleRate);
    for (const target of this.sendSlots()) {
      if (!this.isMixBus(target) || !this.hasSend(stripId, target)) continue;
      const pcol = el("div", "pcol");
      const capEl = el("span", "cap");
      capEl.textContent = SEND_LABEL[target];
      const conn = (): PlanConnection | undefined => sendConnection(this.hooks.getPlan(), stripId, target);
      const factory = sendConnection(this.factoryPlan(), stripId, target)?.params?.pan ?? 0;
      const { busFixed } = mixSendLocks(plan, target);
      const spec = this.panKnobSpec(
        () => conn()?.params?.pan ?? 0,
        (v) => {
          const c = conn();
          if (!c) return;
          c.params = { ...c.params, pan: v };
          // Under Pan Link the send pan is the strip's own PAN / BAL, and turning it moves the
          // strip, as the unit's SEND TO screen does.
          if (mixSendLocks(this.hooks.getPlan(), target).panLinked) panLinkedSendPan(this.hooks.getPlan(), stripId, v);
        },
        factory,
        selfOff ? t().inspector.fx2RateLocked : busFixed ? t().inspector.busFixedSend : undefined,
        undefined,
        selfOff,
      );
      // …and every knob the turn moved shows what the plan now holds: the strip's own PAN, a
      // linked partner's, and the other linked MIX knobs in this popover.
      spec.mirror = () => {
        if (mixSendLocks(this.hooks.getPlan(), target).panLinked) this.repaintPanLinked(stripId);
      };
      // partnerSync off: the mirror is handled by commit; a re-render would tear down
      // this popover, and no partner send-pan control is on screen. Horizontal: the
      // knob steps on Left / Right, and Up / Down walk the popover's knobs.
      const { knob, val } = this.buildKnob(
        spec,
        SEND_LABEL[target],
        stripId,
        "rv",
        controlId(stripId, "pan", target),
        false,
        true,
      );
      pcol.append(capEl, knob, val);
      grid.append(pcol);
    }
    this.sendPanPop.append(ph, grid);
    this.sendPanPop.hidden = false;
    this.sendPanOpenFor = stripId;
    this.sendPanLocks = this.sendPanLockKey(stripId);
    // Mark the trigger active so it reads as the open popover's owner; closeSendPan
    // clears it (the anchor outlives the open/close cycle — a render closes the
    // popover before rebuilding the strips).
    this.sendPanBtn = anchor;
    anchor.classList.add("open");
    anchor.setAttribute("aria-expanded", "true");
    // Anchor below the PAN ▾ button, centred on it (upward caret), clamped to the viewport.
    this.placePopover(this.sendPanPop, anchor, "center", 8);
    if (focusIn) this.focusInto(this.sendPanPop);
  }

  // ---- INS FX popover ----

  /** Open the type popover for a strip, or close it when it is already this strip's.
   *  Both the face and the disclosure call this, so pressing either one twice closes it
   *  — the toggle the meter badge and the PAN button already have. */
  private toggleInsFxPop(id: string, anchor: HTMLElement, focusIn = false): void {
    if (this.typePopFor === id) this.closeTypePop();
    else this.openInsFxPop(id, anchor, focusIn);
  }

  /**
   * The INS FX popover: the effect list, then the bypass switch and the way into the
   * tuning screen.
   *
   * Selection lives here and not on the strip because it is not a switch. Writing the
   * selector makes the unit refill the bound engine array with that type's defaults and
   * that is not reversible, so it is offered as a list of named things with their reasons
   * beside them rather than as something a press cycles through. No Effect leads the list
   * and is never disabled: a strip has to be able to give a slot back from every state,
   * including the three — rate ceiling, STEREO-linked pair, slot taken — that make
   * everything else unpickable.
   */
  private openInsFxPop(id: string, anchor: HTMLElement, focusIn = false): void {
    this.closePopovers();
    const model = this.hooks.getModel();
    const plan = this.hooks.getPlan();
    const menu = insertFxMenu(model, plan, id, this.ifxCensus ?? undefined);
    const eff = effectiveInsertFx(model, plan, id);
    const holds = insertFxSelected({ insertFx: eff });
    this.typePop.replaceChildren();

    const ph = el("div", "ph");
    const cat = el("span", "cat");
    cat.textContent = t().console.insFxType;
    const who = el("span", "who");
    who.textContent = this.toStripModel(id).label;
    ph.append(cat, who);

    const list = el("div", "ilist");
    list.setAttribute("role", "menu");
    this.wireRowKeys(list);
    for (const entry of menu) {
      const isNone = entry.option.value === INSERT_FX_NONE;
      const current = entry.option.value === eff;
      // A lock stops an entry being PICKED, never being left: releasing is what a locked
      // strip needs most. The one it already holds stays pickable-looking for the same
      // reason it stays legible — it is the answer to "what is in this strip", not an
      // offer, and it is marked as checked rather than as available.
      const disabled = entry.lock !== null && !isNone && !current;
      const row = el("div", "irow" + (current ? " active" : "") + (disabled ? " off" : ""));
      row.dataset.ctl = "insfx:" + entry.option.value;
      row.setAttribute("role", "menuitemradio");
      row.setAttribute("aria-checked", String(current));
      const nm = el("span", "nm");
      nm.textContent = entry.option.label;
      row.append(nm);
      // Why it cannot be picked, in the width of a row — or, for No Effect, what picking
      // it does. The full sentence goes on the row's tooltip, which is where the same three
      // facts are said at the width of a panel.
      if (disabled) {
        const why = el("span", "why");
        if (entry.lock === "rate") {
          why.textContent =
            entry.option.maxRate !== undefined ? t().console.insFxMax(formatRate(entry.option.maxRate)) : "";
          row.title =
            entry.option.maxRate !== undefined
              ? t().inspector.insFxRateLockedAt(entry.option.label, formatRate(entry.option.maxRate))
              : t().inspector.insFxRateLocked;
        } else if (entry.lock === "link") {
          why.textContent = t().console.insFxMonoOnly;
          row.title = t().inspector.insFxLinkLocked;
        } else {
          why.textContent = t().console.insFxInUse;
          row.title = t().inspector.insFxSlotLocked;
        }
        row.append(why);
        row.setAttribute("aria-disabled", "true");
      } else if (isNone && holds) {
        const why = el("span", "why");
        why.textContent = t().console.insFxRemove;
        row.append(why);
      }
      if (!disabled) {
        row.tabIndex = 0;
        const pick = (): void => this.setInsFx(id, entry.option.value);
        row.addEventListener("click", pick);
        row.addEventListener("keydown", (e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            pick();
          }
        });
      }
      list.append(row);
    }

    // The launcher, under the list it depends on: inert while the strip holds nothing,
    // because there is no effect for a screen to show and the way out of that state is the
    // list above it.
    //
    // The BYPASS is deliberately not here. It used to sit beside the launcher, and it is
    // the same switch as the strip's own INS FX face — one control shown twice, kept in
    // step by hand because a render would tear the popover down. Two faces for one value
    // is a question the operator has to answer ("are these the same?") before they can use
    // either, and the answer was yes.
    const foot = el("div", "ifoot");
    // Gated on the screen being openable, not on the strip holding SOMETHING. The two
    // agree today — every family the app edits has a screen — and they did not when the
    // multi-band compressor had none: on that strip the row was live, said "open me" and
    // did nothing at all when pressed, because the screen's own bind returned null and the
    // host returned silently. The Inspector's launcher asks this question; this one asked
    // an easier one, and the easier one is what stops being true first.
    const openable = insertFxScreenFamily(model, plan, id) !== null;
    const open = el("div", "iopen" + (openable ? "" : " off"));
    open.dataset.ctl = "open-screen";
    open.textContent = dynOpenLabel("insfx", t());
    if (openable) {
      open.setAttribute("role", "button");
      // A bypassed or rate-stopped effect is still an effect to tune, and the screen says
      // so under its own display. Only a strip holding nothing has nothing to open.
      this.wireActivate(open, undefined, () => {
        this.closeTypePop();
        this.hooks.onOpenDynScreen?.("insfx", id);
      });
    } else {
      open.setAttribute("aria-disabled", "true");
      open.title = t().console.insFxPickFirst;
    }
    foot.append(open);

    this.typePop.append(ph, list, foot);
    this.typePop.hidden = false;
    this.typePopFor = id;
    this.typePopKind = "insfx";
    this.typePopBtn = anchor;
    anchor.classList.add("open");
    anchor.setAttribute("aria-expanded", "true");
    this.placePopover(this.typePop, anchor, "center", 8);
    if (focusIn) this.focusInto(this.typePop);
  }

  private fxTypeOpenChip(id: string): HTMLElement {
    const chip = el("div", "con-chip con-chip-open con-fxopen");
    chip.dataset.ctl = "open:fx";
    // Always "▸": an FX channel always holds an effect, so this never stands for the "+"
    // the INS FX opener shows over an empty strip.
    chip.textContent = "▸";
    chip.setAttribute("role", "button");
    chip.setAttribute("aria-haspopup", "menu");
    chip.setAttribute("aria-expanded", "false");
    const label = t().inspector.fxEffect.effectType;
    chip.title = label;
    chip.setAttribute("aria-label", label);
    this.wireActivate(chip, undefined, (viaKey) => {
      if (this.typePopFor === id && this.typePopKind === "fx") this.closeTypePop(true);
      else this.openFxTypePop(id, chip, viaKey);
    });
    return chip;
  }

  /** The face beside that disclosure: lit, and not a control. It carries the read-only
   *  chip's accessible shape (a pressed button that is disabled, out of the tab order) with
   *  the tooltip as its reason, but not its dimming — `.static` keeps the lit look and only
   *  takes away the pointer. Nothing is wired, so a press, a key and MIDI learn all pass it
   *  by. */
  private fxEffectFaceChip(): HTMLElement {
    const chip = el("div", "con-chip con-fxface static on");
    chip.dataset.ctl = "fx-face";
    chip.textContent = t().console.effect;
    chip.setAttribute("role", "button");
    chip.setAttribute("aria-pressed", "true");
    chip.setAttribute("aria-disabled", "true");
    setTip(chip, t().console.effectHint);
    chip.tabIndex = -1;
    return chip;
  }

  /**
   * The EFFECT TYPE popover: the same element and the same three parts the INS FX one uses.
   *
   * Three things that list has and this one does not, all for one reason — an FX channel's
   * selector has no "no effect" value and its engine is its own:
   *   - no empty row, so every entry is an effect;
   *   - no row disabled on its own account. Both channels' five types run at every rate the
   *     bus itself runs at (the effect list gives FX1's five 176.4 / 192 kHz and FX2's five a
   *     96 kHz ceiling, which is the rate the FX2 bus stops at anyway), and there is no shared
   *     slot for a second holder to take. So there is no `why` column here at all — only the
   *     bus itself, gone above 96 kHz, holds every row but the current one;
   *   - the launcher is never inert, because there is always an effect to open.
   *
   * There is no ON row here, and the INS FX popover's bypass has no counterpart: an FX
   * channel's effect has no switch of its own beside the strip's [ON].
   */
  private openFxTypePop(id: string, anchor: HTMLElement, focusIn = false): void {
    this.closePopovers();
    const plan = this.hooks.getPlan();
    const fxIndex = FX_CHANNEL_NODE_INDEX[id];
    const cur = resolveFxEffectType(fxIndex, plan.nodeParams[id]?.fxEffect?.type);
    this.typePop.replaceChildren();

    const ph = el("div", "ph");
    const cat = el("span", "cat");
    cat.textContent = t().inspector.fxEffect.effectType;
    const who = el("span", "who");
    who.textContent = this.toStripModel(id).label;
    ph.append(cat, who);

    const list = el("div", "ilist");
    list.setAttribute("role", "menu");
    this.wireRowKeys(list);
    const rateOff = nodeRateDisabled(id, plan.sampleRate);
    for (const option of fxEffectTypes(fxIndex)) {
      const current = option.value === cur;
      const row = el("div", "irow" + (current ? " active" : "") + (rateOff && !current ? " off" : ""));
      row.dataset.ctl = "fx:" + option.value;
      row.setAttribute("role", "menuitemradio");
      row.setAttribute("aria-checked", String(current));
      const nm = el("span", "nm");
      nm.textContent = option.label;
      row.append(nm);
      if (rateOff) {
        row.title = t().inspector.fx2RateLocked;
        row.setAttribute("aria-disabled", "true");
        list.append(row);
        continue;
      }
      row.tabIndex = 0;
      const pick = (): void => this.setFxType(id, option.value);
      row.addEventListener("click", pick);
      row.addEventListener("keydown", (e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          pick();
        }
      });
      list.append(row);
    }

    const foot = el("div", "ifoot");
    const open = el("div", "iopen");
    open.dataset.ctl = "open-screen";
    open.textContent = dynOpenLabel("fx", t());
    open.setAttribute("role", "button");
    // An effect on a bus the rate has taken away still opens: the screen holds its rows
    // read-only and says why under its own display, so nothing gates this.
    this.wireActivate(open, undefined, () => {
      this.closeTypePop();
      this.hooks.onOpenDynScreen?.("fx", id);
    });
    foot.append(open);

    this.typePop.append(ph, list, foot);
    this.typePop.hidden = false;
    this.typePopFor = id;
    this.typePopKind = "fx";
    this.typePopBtn = anchor;
    anchor.classList.add("open");
    anchor.setAttribute("aria-expanded", "true");
    this.placePopover(this.typePop, anchor, "center", 8);
    if (focusIn) this.focusInto(this.typePop);
  }

  /**
   * Write an EFFECT TYPE.
   *
   * The plan loses nothing across this: an FX channel keeps every family's values side by
   * side under keys that carry the family name, so the outgoing effect's settings are still
   * there and a type of that family finds them again. Inside one family the keys are shared,
   * so the three Rev-X types read one another's values rather than parking their own. What the UNIT does is the opposite — it
   * refills the engine array with the incoming type's factory values — and the writer puts
   * the plan back over that immediately, so the two agree again.
   *
   * The one thing that would genuinely be lost is a value the UNIT holds and the plan has
   * never seen, because the effect arrays announce nothing when the front panel moves them.
   * That is read off the unit at the write boundary rather than here (`live.ts`, the park in
   * front of a head write), so this selection is an ordinary plan edit and every other way
   * of moving the same value — the inspector's row, an undo of either — is covered by the
   * same read.
   */
  private setFxType(id: string, value: number): void {
    this.closeTypePop();
    this.hooks.onSetFxEffectType(id, value);
    this.render();
    // …and the screen opens on it, which is what the INS FX popover does after a selection.
    // The two are the app's only Type axis and they are reached the same way, so a press that
    // means the same thing on both cannot land somewhere different. An FX channel always
    // holds an effect, so there is no state here where the screen would have nothing to show.
    this.hooks.onOpenDynScreen?.("fx", id);
  }

  private closeTypePop(restore = false): void {
    if (!this.typePopFor) return;
    const openFor = this.typePopFor;
    this.typePopFor = null;
    this.typePop.hidden = true;
    this.typePop.replaceChildren();
    if (this.typePopBtn) {
      this.typePopBtn.classList.remove("open");
      this.typePopBtn.setAttribute("aria-expanded", "false");
      // The disclosure if the rebuilt strip still has one, and on an INS FX strip the face
      // otherwise — the same order every other landing here uses.
      this.releaseFocus(this.typePopBtn, restore, openFor, this.typePopSel.join(", "));
      this.typePopBtn = null;
    }
  }

  private closeSendPan(restore = false): void {
    if (!this.sendPanOpenFor) return;
    const openFor = this.sendPanOpenFor;
    this.sendPanOpenFor = null;
    this.sendPanPop.hidden = true;
    this.sendPanPop.replaceChildren();
    if (this.sendPanBtn) {
      this.sendPanBtn.classList.remove("open");
      this.sendPanBtn.setAttribute("aria-expanded", "false");
      this.releaseFocus(this.sendPanBtn, restore, openFor, ".con-panbtn");
      this.sendPanBtn = null;
    }
  }

  // Position a fixed popover by its anchor, clamped to the viewport: opens `gap` px
  // below the anchor (flipping above on bottom overflow); `align` picks the horizontal
  // edge — "right" (the popover's right under the anchor's right, for the meter badge)
  // or "center" (centred on the anchor, for the SEND PAN button).
  private placePopover(pop: HTMLElement, anchor: HTMLElement, align: "right" | "center", gap: number): void {
    const r = anchor.getBoundingClientRect();
    const pw = pop.offsetWidth;
    const left = popLeft(align === "center" ? r.left + r.width / 2 - pw / 2 : r.right - pw, pw);
    pop.style.left = left + "px";
    const top = popTop(r, pop.offsetHeight, gap);
    pop.style.top = top + "px";
    // Caret side follows the flip: `.below` when it opened below the anchor, `.above`
    // when bottom overflow flipped it above (the SEND PAN popover styles the caret).
    const below = top >= r.bottom;
    pop.classList.toggle("below", below);
    pop.classList.toggle("above", !below);
  }

  // ---- MIDI learn ----

  /** In learn mode an armable control arms itself on activation instead of
   *  editing. Returns true when the interaction was consumed by arming. */
  private midiArm(id: string | undefined): boolean {
    const midi = this.hooks.midi;
    if (!id || !midi?.learnActive()) return false;
    midi.arm(id);
    return true;
  }

  /** Learn-mode keyboard gate for the fader / knob handlers: Space/Enter arms;
   *  anything else (Tab, arrows) is left to the browser so keyboard navigation
   *  keeps working. True when learn mode owns the event (skip the edit keys). */
  private midiLearnKey(e: KeyboardEvent, midiId: string | undefined): boolean {
    if (!this.hooks.midi?.learnActive()) return false;
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      this.midiArm(midiId);
    }
    return true;
  }

  /** Learn-mode affordances plus the bound address as a tooltip — the shared
   *  treatment, so a strip control and a tuning-screen control read the same. */
  private midiMark(el: HTMLElement, id: string | undefined): void {
    markMidi(el, id, this.hooks.midi);
  }

  // Paint a scribble with the node's device CH SETTING colour, or with the rail
  // fallback when unset — and pick the ink for whichever ground it ends up being.
  // The fallback used to return early, which left every rail-coloured scribble on
  // the stylesheet's fixed ink and no halo at all: measured, four of the five rails
  // are mid-tones where that ink loses by 30-40 Lc, and two of them reach neither
  // ink's floor, which is exactly where the halo does the work.
  private paintScribble(scrib: HTMLElement, m: StripModel): void {
    const color = planColorHex(this.hooks.getPlan().nodeColors?.[m.id]);
    const ink = color ? inkOn(color) : this.inkForRail(m.rail);
    if (color) scrib.style.background = color;
    if (!ink) return;
    scrib.style.color = ink.color;
    scrib.style.setProperty("--scrib-shadow", ink.shadow);
  }

  // Resolve a rail token to its ink. `rail` is the token reference the strip writes
  // (`var(--rail-channel)`), so the colour itself lives in the stylesheet and only
  // the computed root can say what it is. That read is why this is cached and why
  // render() drops the cache: reading it per strip would put a computed-style read
  // inside the rebuild loop, which is the shape that costs 25 ms a layout in WebKit.
  private inkForRail(rail: string): { color: string; shadow: string } | null {
    const token = /^var\((--[a-z0-9-]+)\)$/.exec(rail)?.[1];
    if (!token) return null;
    const hit = this.railInk.get(token);
    if (hit) return hit;
    const hex = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    if (!hex) return null;
    const ink = inkOn(hex);
    this.railInk.set(token, ink);
    return ink;
  }

  // The scribble strip: the CH SETTING colour + a power LED, the node name and the
  // device CH SETTING name row ("—" when unset, so every strip is the same height).
  // Shared by both strip builders. When the strip has an on/off (every strip but
  // STREAMING) the LED is the power button, its hit area reaching from the strip's
  // corner to the name text and down to the chip row; the name text itself is not.
  private scribble(m: StripModel): HTMLElement {
    const scrib = el("div", "con-scribble");
    this.paintScribble(scrib, m);
    const name = el("div", "name");
    const spec = this.powerSpec(m);
    let power: HTMLElement | undefined;
    let led: HTMLElement | undefined;
    if (spec) {
      power = el("span", "con-power");
      led = el("span", "con-pled");
      led.append(el("i", "dot"));
      power.append(led);
      name.append(power);
    }
    const txt = el("span", "txt");
    const sides = this.sideParts(m);
    if (sides) {
      // A stereo channel's name switches the side the strip shows; the hidden side's number dims.
      const shown = displayedSide(m.id);
      const [l, r] = [el("span", ""), el("span", "")];
      l.textContent = sides.l;
      r.textContent = sides.r;
      (shown === "L" ? r : l).classList.add("dim");
      txt.append(sides.prefix, l, "/", r);
      txt.classList.add("con-side");
      txt.dataset.ctl = "side";
      txt.setAttribute("role", "button");
      const model = this.hooks.getModel();
      const which = shown === "L" ? t().inspector.sideL : t().inspector.sideR;
      txt.setAttribute("aria-label", spoken(t().console.sideShowing(m.label, which(sideLabel(model, m.id, shown)))));
      this.wireActivate(txt, undefined, () => this.hooks.onSwitchSide?.(m.id, shown === "L" ? "R" : "L"));
    } else {
      txt.textContent = m.label;
    }
    // The LED steals ~2 chars; shrink long names a step (or two for OSCILLATOR) so
    // they fit beside it. "CH 11/12" (8 chars) overflows 11px by ~1px in SF Mono, so
    // 8-char names drop to 9px; STREAMING has no LED (spec null), so its 9-char name
    // stays full-size.
    if (spec && m.label.length >= 8) txt.style.fontSize = m.label.length >= 10 ? "8px" : "9px";
    name.append(txt);
    const dev = el("div", "id");
    dev.textContent = m.deviceName || "—";
    if (!m.deviceName) dev.classList.add("empty");
    scrib.append(name, dev);
    if (spec && power && led) this.wirePower(power, led, m, spec);
    return scrib;
  }

  // A stereo channel's name split into its two sides' numbers: "CH " + "11" + "/" + "12", read
  // off the model's side labels. Null for any other strip, and for a label the side labels do
  // not compose, which then shows as it is.
  private sideParts(m: StripModel): { prefix: string; l: string; r: string } | null {
    if (!m.isChannel || !isStereoChannel(m.id)) return null;
    const model = this.hooks.getModel();
    const [left, right] = [sideLabel(model, m.id, "L"), sideLabel(model, m.id, "R")];
    if (!m.label.startsWith(`${left}/`)) return null;
    const r = m.label.slice(left.length + 1);
    const prefix = right.slice(0, right.length - r.length);
    if (!r || !right.endsWith(r) || !left.startsWith(prefix) || left === prefix) return null;
    return { prefix, l: left.slice(prefix.length), r };
  }

  // The strip's power control (the scribble LED): the node master ON on np.on — a
  // CH_ON / FX / MIX 675, or a STEREO / MONITOR master — or the oscillator on
  // osc.on. STREAMING has no master, so it gets no LED. Every non-OSC strip arms
  // the same "chOn" id (see below); "mute" is the separate → STEREO send.
  private powerSpec(m: StripModel): PowerSpec | null {
    if (m.isStream) return null;
    if (m.isOsc) {
      return {
        on: this.hooks.getPlan().nodeParams[m.id]?.osc?.on === true,
        toggle: () => {
          const p = this.nodeParamsOf(m.id);
          p.osc = { ...p.osc, on: !(p.osc?.on === true) };
          return ["osc.on"];
        },
        midiId: controlId(m.id, "oscOn"),
      };
    }
    // Every non-OSC strip's power LED is "chOn" (np.on, ON polarity) — uniform, so
    // the on-screen LED and the controller LED never disagree on polarity. ("mute" on
    // CH / FX / MIX is the separate → STEREO send.)
    return {
      on: this.hooks.getPlan().nodeParams[m.id]?.on !== false,
      toggle: () => {
        const p = this.nodeParamsOf(m.id);
        p.on = p.on === false;
        return ["on"];
      },
      midiId: controlId(m.id, "chOn"),
    };
  }

  // Wire an element as an activatable button: keyboard (Space / Enter), MIDI-learn
  // mark + arming, and click. `run` performs the edit, told whether the keyboard
  // activated it; in learn mode arming consumes the activation instead. Shared by the
  // toggle chips and the scribble's power LED.
  private wireActivate(el: HTMLElement, midiId: string | undefined, run: (viaKey: boolean) => void): void {
    el.tabIndex = 0;
    this.midiMark(el, midiId);
    const activate = (viaKey: boolean): void => {
      if (this.midiArm(midiId)) return;
      run(viaKey);
    };
    el.addEventListener("click", () => activate(false));
    el.addEventListener("keydown", (e) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        activate(true);
      }
    });
  }

  // A read-only control that stays a MIDI-learn target: learn mode marks it, puts it in the tab
  // order and arms it on a press; outside learn mode a press does nothing. The read-only chip
  // takes the same treatment in `buildChip`.
  private learnOnly(el: HTMLElement, midiId: string | undefined): void {
    if (!midiId) return;
    this.wireActivate(el, midiId, () => {});
    el.tabIndex = this.hooks.midi?.learnActive() ? 0 : -1;
  }

  // Make the LED a power button: the LED reflects the on-state, click / Enter / Space
  // toggle the node master (or oscillator) through the shared funnel. A full re-render
  // follows so the strip's inactive dim updates. The button's class carries no state —
  // the lit state is the LED's — so the focus restore after that render finds it by class.
  private wirePower(power: HTMLElement, led: HTMLElement, m: StripModel, spec: PowerSpec): void {
    led.classList.toggle("on", spec.on);
    power.dataset.ctl = spec.midiId;
    power.setAttribute("role", "button");
    power.setAttribute("aria-pressed", String(spec.on));
    power.setAttribute("aria-label", spoken(`${m.label} ${t().console.power}`));
    // A node the rate removes keeps its LED as it stands and refuses the press.
    if (nodeRateDisabled(m.id, this.hooks.getPlan().sampleRate)) {
      power.setAttribute("aria-disabled", "true");
      setTip(power, t().inspector.fx2RateLocked);
      this.learnOnly(power, spec.midiId);
      return;
    }
    this.wireActivate(power, spec.midiId, () => {
      this.commit(m.id, spec.toggle());
      this.render();
    });
  }

  // A toggle chip ("MUTE"/"EQ"/"ON"/…): role=button, aria-pressed, keyboard-activated;
  // `toggle` flips the underlying plan flag and returns the new state, then the chip
  // commits (and re-renders when commit asks). Appends to `parent`; see buildChip for
  // the returning variant (the SENDS rack builds columns before appending).
  private makeChip(
    id: string,
    parent: HTMLElement,
    label: string,
    mute: boolean,
    on: boolean,
    toggle: () => boolean,
    opts?: {
      readonlyTitle?: string;
      midiId?: string;
      title?: string;
      rerender?: boolean;
      keys?: readonly string[];
      spoken?: string;
    },
  ): void {
    parent.append(this.buildChip(id, label, on, toggle, { ...opts, mute }));
  }

  /** The narrow chip beside GATE / COMP that opens that processor's tuning screen.
   *  Momentary, so it deliberately skips `buildChip`: that runs `commit()` after
   *  its toggle, and this button changes nothing to commit. `wireActivate` still
   *  gives it the keyboard activation and the MIDI-learn guard the chips have —
   *  with no `midiId`, since a screen is not a device parameter to map. */
  /**
   * Append the narrow half of a face + disclosure pair, and mark the face as PAIRED.
   *
   * `chip` is null where the pair EXISTS but the disclosure is withheld — a rate ceiling
   * that leaves nothing behind it to open. The slot is then filled by a hidden placeholder
   * of the same width, so the row's geometry is the same either way, and the face carries
   * its own width rather than one read off whichever neighbour happens to be there.
   *
   * A processor with no screen AT ALL does not come through here: its face is one of the
   * two-per-row chips and takes that width, which is the shape it has always had.
   */
  private appendOpener(proc: HTMLElement, chip: HTMLElement | null): void {
    // A pair spans a whole row, so it has to START one. Landing in the second column, it
    // wraps and the chip before it is left alone on its row — where `.con-chip`'s grow
    // stretches it to full width, silently, because nothing here is an error. The filler
    // goes in FRONT of the face, so every pair is covered the day it is written rather
    // than at whichever call site remembered.
    const face = proc.lastElementChild;
    if (face && proc.childElementCount % 2 === 0) face.before(el("div", "con-chip spacer"));
    face?.classList.add("paired");
    proc.append(chip ?? el("div", "con-chip con-chip-open spacer"));
  }

  private dynOpenChip(kind: DynKind, id: string): HTMLElement {
    const chip = el("div", "con-chip con-chip-open");
    chip.dataset.ctl = "open:" + kind;
    chip.textContent = "▸";
    chip.setAttribute("role", "button");
    // What it opens, for `focusOpener` to find it by after a rebuild.
    chip.dataset.dynOpen = kind;
    chip.dataset.dynNode = id;
    const label = dynOpenLabel(kind, t());
    chip.title = label;
    chip.setAttribute("aria-label", label);
    this.wireActivate(chip, undefined, () => this.hooks.onOpenDynScreen?.(kind, id));
    return chip;
  }

  /** The INS FX face on a strip holding nothing. It looks like a chip and is not one:
   *  there is no insert to switch in or out, so pressing it opens the same type popover
   *  the disclosure beside it opens. Deliberately NOT a switch that picks the first free
   *  effect — that wrote a selection the operator never chose, and a selector write is
   *  what makes the unit refill the engine array with that type's defaults.
   *
   *  `whyNone` is why nothing can be taken right now, where that is the case. It goes on
   *  a face that stays OPERABLE: releasing has to be reachable from every state, and the
   *  list behind it is where the reason is said per effect. Saying it here as well is
   *  what keeps "why is this strip empty" answerable without opening anything. */
  /** `locked` is what the RATE forbids, and it is not the same as `whyNone`: a strip whose
   *  menu is empty because every slot is taken still opens a popover worth reading (it
   *  names what holds them), while a rate above every ceiling leaves nothing to choose at
   *  all. Only the second goes inert — an opener that opens an empty menu is the shape this
   *  was: it said why in a tooltip and then opened anyway. */
  private insFxVacantChip(id: string, whyNone?: string, locked?: boolean): HTMLElement {
    const chip = el("div", IFX_VACANT_CLS + (locked ? " readonly" : ""));
    chip.dataset.ctl = "ifx-face";
    chip.textContent = "INS FX";
    chip.setAttribute("role", "button");
    chip.setAttribute("aria-haspopup", "menu");
    chip.setAttribute("aria-expanded", "false");
    if (whyNone) chip.title = whyNone;
    if (locked) {
      chip.setAttribute("aria-disabled", "true");
      // As `buildChip`'s read-only face: out of the tab order, still able to receive focus,
      // so a popover that closes onto this strip has somewhere to land.
      chip.tabIndex = -1;
      return chip;
    }
    this.wireActivate(chip, undefined, (viaKey) => this.toggleInsFxPop(id, chip, viaKey));
    return chip;
  }

  /** The disclosure beside the INS FX face. `▸` is the processing row's own glyph and
   *  this keeps it, but it is the one in that row that does not open a screen: the type
   *  has to be settled before a screen has anything to show, so it opens the popover and
   *  the screen is a second press inside it. `+` in place of `▸` where the strip holds
   *  nothing, since what it offers there is a choice rather than a way in. */
  private insFxOpenChip(id: string, holds: boolean): HTMLElement {
    const chip = el("div", "con-chip con-chip-open con-ifxopen");
    chip.dataset.ctl = "open:insfx";
    chip.textContent = holds ? "▸" : "+";
    chip.setAttribute("role", "button");
    chip.setAttribute("aria-haspopup", "menu");
    chip.setAttribute("aria-expanded", "false");
    const label = t().inspector.insertFx;
    chip.title = label;
    chip.setAttribute("aria-label", label);
    this.wireActivate(chip, undefined, (viaKey) => this.toggleInsFxPop(id, chip, viaKey));
    return chip;
  }

  // The chip primitive, returning the element. `cls` picks the base class (con-chip
  // for the head chips, con-sl / con-slp for the rack's enable chip / PRE button);
  // opts.mute paints the MUTE colour, opts.after runs after the toggle (before commit),
  // opts.readonlyTitle renders it inert with a tooltip, opts.midiId arms MIDI learn,
  // opts.rerender rebuilds the whole view for a toggle whose effect reaches other strips,
  // opts.ctl names the chip for the focus carry-over (its MIDI id, else its label, when unset).
  private buildChip(
    id: string,
    label: string,
    on: boolean,
    toggle: () => boolean,
    opts?: {
      cls?: string;
      mute?: boolean;
      readonlyTitle?: string;
      midiId?: string;
      title?: string;
      after?: (next: boolean) => void;
      rerender?: boolean;
      keys?: readonly string[];
      ctl?: string;
      /** The word a symbol `label` is announced as (see setNamedText). */
      spoken?: string;
    },
  ): HTMLElement {
    const { cls = "con-chip", mute, readonlyTitle, midiId, title, after, rerender, keys, ctl, spoken } = opts ?? {};
    // Normalised before it reaches the DOM. The device write is `np.<flag> ? 1 : 0`
    // and the load funnel passes a finite numeric leaf through unchecked, so a plan
    // authored elsewhere reaches here carrying 1 — `String(1)` is "1", which is not
    // an ARIA boolean, and a screen reader is told nothing rather than "pressed".
    const state = Boolean(on);
    const chip = el("div", cls + (mute ? " mute" : "") + (state ? " on" : "") + (readonlyTitle ? " readonly" : ""));
    if (spoken) setNamedText(chip, label, spoken);
    else chip.textContent = label;
    chip.dataset.ctl = ctl ?? midiId ?? label;
    chip.setAttribute("role", "button");
    chip.setAttribute("aria-pressed", String(state));
    // A hover tooltip spelling out a terse label (e.g. C.INT → Cue Interrupt).
    if (title) setTip(chip, title);
    if (readonlyTitle) {
      chip.setAttribute("aria-disabled", "true");
      setTip(chip, readonlyTitle);
      // Out of the tab order — there is nothing to operate — but still able to RECEIVE
      // focus, so it can be the place a closing popover leaves the operator. Without this
      // a chip the rate turned read-only is unfocusable, and the focus falls to <body>.
      chip.tabIndex = -1;
      // A read-only chip given a `midiId` stays a MIDI-learn target: learn mode marks it,
      // puts it in the tab order and arms it on a press; outside learn mode a press does nothing.
      if (midiId) {
        this.wireActivate(chip, midiId, () => {});
        chip.tabIndex = this.hooks.midi?.learnActive() ? 0 : -1;
      }
      return chip;
    }
    this.wireActivate(chip, midiId, () => {
      const next = toggle();
      chip.classList.toggle("on", next);
      chip.setAttribute("aria-pressed", String(next));
      after?.(next);
      const mirrored = this.commit(id, keys);
      if (mirrored || rerender) this.render();
      // A C.INT press decides whether its MONITOR plays the CUE bus.
      else this.syncCues();
    });
    return chip;
  }

  /**
   * A strip's [CUE] chip, or null for a strip that has none. Lit while the strip is cued. It
   * switches the unit's flag rather than the plan, so a press commits nothing; with no session
   * up it is read-only, and a MIDI-learn target still, so a control can be assigned to it.
   */
  private cueChip(id: string): HTMLElement | null {
    const cue = this.hooks.cue;
    if (!cue || !cueAddrOf(this.hooks.getModel(), id)) return null;
    const midiId = controlId(id, "cue");
    const on = cue.has(id);
    let chip: HTMLElement;
    if (!cue.enabled()) {
      chip = this.buildChip(id, t().console.cueChip, on, () => on, {
        readonlyTitle: t().console.cueOffline,
        midiId,
      });
    } else {
      chip = el("div", "con-chip" + (on ? " on" : ""));
      chip.textContent = t().console.cueChip;
      chip.dataset.ctl = midiId;
      chip.setAttribute("role", "button");
      chip.setAttribute("aria-pressed", String(on));
      this.wireActivate(chip, midiId, () => cue.set(id, !cue.has(id)));
    }
    chip.classList.add("con-cue");
    chip.dataset.cue = id;
    return chip;
  }

  /** Repaint what shows the cues without rebuilding a strip: each CUE chip's light, and the
   *  frame and C.INT colour of a MONITOR whose Cue Interrupt makes it play the CUE bus. */
  syncCues(): void {
    const cue = this.hooks.cue;
    if (!cue) return;
    for (const chip of this.host.querySelectorAll<HTMLElement>(".con-cue[data-cue]")) {
      const on = cue.has(chip.dataset.cue!);
      chip.classList.toggle("on", on);
      chip.setAttribute("aria-pressed", String(on));
    }
    const plan = this.hooks.getPlan();
    for (const strip of this.host.querySelectorAll<HTMLElement>("[data-cue-monitor]")) {
      const id = strip.dataset.cueMonitor!;
      const taking = cue.any() && (plan.nodeParams[id]?.cueInterrupt ?? true);
      strip.classList.toggle("cue-take", taking);
      const cint = strip.querySelector<HTMLElement>(`[data-ctl="${controlId(id, "cueInterrupt")}"]`);
      if (!cint) continue;
      cint.classList.toggle("cue-int", taking);
      setTip(cint, taking ? t().console.cueTaking : t().console.cueFull);
    }
  }

  // Build one lane: its bar column (green→red LED bar + shade + peak marker) and its
  // OVER latch cell. `side` is "" for a mono strip's single lane, or "l"/"r" to place
  // it in a stereo pair (both lanes then sit in the shared ladder / OVER frames).
  private buildLane(side: string): MeterLane {
    const col = el("div", "mtrcol" + (side ? " " + side : ""));
    const bar = el("div", "bar");
    const shade = el("div", "shade");
    const peak = el("div", "peak");
    col.append(bar, shade, peak);
    const clip = el("div", "lit" + (side ? " " + side : ""));
    return { col, shade, peak, clip, v: 0, pk: 0, over: 0, lv: -1, lpk: -1, lov: -1, live: false };
  }

  // Build the meter column: one OVER frame + one ladder frame, each holding one lane
  // (mono) or two (stereo L/R side by side with a gap). `stereo` splits the bars and
  // the clip cells but keeps the framing undivided. Returns the lanes paintMeters drives.
  private buildMeterColumn(range: LevelRange, stereo: boolean): { meter: HTMLElement; lanes: MeterLane[] } {
    const meter = el("div", "con-meter" + (stereo ? " stereo" : ""));
    // The ladder spans from the scale's lowest tick (--mfloor) to the 0 dB mark
    // (--mzero); the OVER window sits above. Both share the strip's fader ruler.
    meter.style.setProperty("--mzero", dbToFrac(0, range) * 100 + "%");
    meter.style.setProperty("--mfloor", dbToFrac(range.ticks[range.ticks.length - 1], range) * 100 + "%");
    const over = el("div", "con-over");
    const ladder = el("div", "con-ladder sig");
    // Color-zone boundaries as a fraction of the ladder, at the same travel as the dB
    // ticks of the matching value — so green/yellow/red map to absolute dBFS, not to
    // the lit height. Set on the frame; the bars inherit them.
    ladder.style.setProperty("--zy", meterFrac(METER_GREEN_TOP_DB, range) * 100 + "%");
    ladder.style.setProperty("--zr", meterFrac(METER_YELLOW_TOP_DB, range) * 100 + "%");
    const lanes = stereo ? [this.buildLane("l"), this.buildLane("r")] : [this.buildLane("")];
    for (const ln of lanes) {
      over.append(ln.clip);
      ladder.append(ln.col);
    }
    meter.append(over, ladder);
    return { meter, lanes };
  }

  // STREAMING strip: a live meter only — no fader, no set-level readout, no chips
  // (the device offers no level/EQ here, just a source select + delay). One meter
  // point (the device exposes only the post-DELAY meter), so no tap selector either.
  private buildMeterOnlyStrip(m: StripModel): HTMLElement {
    // OSC rests off by default, so its strip is dimmed until switched on (via the
    // scribble power LED) — the same inactive dim as every other strip. STREAMING has
    // no on/off, so it never dims (m.inactive is false there).
    const strip = el("div", "con-strip meter-only" + (m.inactive ? " inactive" : ""));
    nameStrip(strip, m);
    strip.style.setProperty("--rail", m.rail);

    const head = el("div", "con-head");
    head.append(this.scribble(m));
    // OSCILLATOR: a LEVEL knob replaces the fader (the ON/OFF is the scribble power
    // LED). Edits the plan and syncs live via commit().
    if (m.isOsc) {
      // LEVEL knob: full OSC range (-96…0 dB). The indicator's horizontal marks read
      // -50 (left) / -8 (right); the extremes (down-left / down-right) reach -96 / 0.
      const factory = this.factoryPlan().nodeParams[m.id]?.osc?.level ?? -14;
      this.addKnob(
        head,
        "LEVEL",
        {
          get: () => this.getMain(m),
          set: (v) => this.setMain(m, v),
          min: -96,
          max: 0,
          step: 1,
          format: formatLevelNumber,
          reset: factory,
          angle: (v) =>
            v <= -50 ? -135 + ((v + 96) / 46) * 45 : v >= -8 ? 90 + ((v + 8) / 8) * 45 : -90 + ((v + 50) / 42) * 180,
        },
        m.id,
        controlId(m.id, "level"),
      );
    }
    // STREAMING: a DELAY on/off chip and a TIME knob (the delay time, 1…1000 ms).
    // Gives the otherwise-bare head controls so the strip reads as purposeful, and
    // mirrors the OSCILLATOR's ON + LEVEL pairing. A key or a notch moves 1.00 ms and
    // holding Shift 0.02 ms (push-and-turn), each keeping the hundredths the way the unit's
    // own knob does, and a drag lands on that 0.02 ms grid.
    if (m.isStream) {
      const chips = el("div", "con-chips");
      const delayOn = (): boolean => this.hooks.getPlan().nodeParams[m.id]?.delay?.on ?? false;
      this.makeChip(
        m.id,
        chips,
        "DELAY",
        false,
        delayOn(),
        () => {
          const np = this.nodeParamsOf(m.id);
          const next = !delayOn();
          np.delay = { ...np.delay, on: next };
          return next;
        },
        { keys: ["delay.on"] },
      );
      chips.append(this.cueChip(m.id) ?? el("div", "con-chip spacer"));
      head.append(chips);
      const factory = this.factoryPlan().nodeParams[m.id]?.delay?.time ?? DELAY_TIME_MIN_MS;
      this.addKnob(
        head,
        "TIME",
        {
          get: () => this.hooks.getPlan().nodeParams[m.id]?.delay?.time ?? DELAY_TIME_MIN_MS,
          // The time the plan takes is the one the unit holds, on its 0.01 ms grid.
          set: (v) => {
            const np = this.nodeParamsOf(m.id);
            np.delay = { ...np.delay, time: vdToDelayTime(delayTimeToVd(v)) };
          },
          keys: ["delay.time"],
          min: DELAY_TIME_MIN_MS,
          max: DELAY_TIME_MAX_MS,
          step: 1, // ±1.00 ms per detent, keeping the hundredths
          fine: DELAY_TIME_GRID_MS, // device-verified fine grid (fixed, rate-independent)
          grid: DELAY_TIME_GRID_MS,
          // Digits by need: off-grid (fine / inspector-set) values get both
          // decimals; whole values keep the original compact display.
          format: (v) => v.toFixed(v % 1 ? 2 : v < 100 ? 1 : 0),
          reset: factory,
        },
        m.id,
      );
    }
    strip.append(head);
    // A meter-only strip has no sends, so its rack is the dimmed SENDS header only —
    // but it reserves the same rack height as every other strip so the fader/meter
    // tops stay aligned (and the global collapse is reachable from its header too).
    strip.append(this.buildSendRack(m).el);

    const zone = el("div", "con-faderzone");
    zone.append(el("div", "con-taphead")); // empty: keeps fader/meter tops aligned
    const zrow = el("div", "con-zrow");
    const tap = tapFor(m.id, this.tapKeyOf(m.id), this.hooks.getModel().id) ?? null;
    const { meter, lanes } = this.buildMeterColumn(m.range, isStereoTap(tap));
    // Meter tops out at 0 dBFS and there is no fader, so the scale stops at 0.
    zrow.append(this.buildScale(m.range, 0), meter);
    zone.append(zrow);
    strip.append(zone);

    // readout: live meter value only (no fader set-level cell).
    const readout = el("div", "con-readout");
    const { cell: mtrCell, value: mtrEl } = meterReadCell();
    readout.append(mtrCell);
    strip.append(readout);

    this.refs.set(m.id, {
      m,
      root: strip,
      lanes,
      readMtr: mtrEl,
      tap,
      sig: { lmtr: 1 },
    });
    return strip;
  }

  private render(): void {
    // The rail inks are resolved from the computed root, so they are theme-dependent
    // and a render is the only thing that re-derives them. A theme switch does not
    // trigger one (see applyResolvedTheme in main.ts for why that is currently safe
    // and what would end it).
    this.railInk.clear();
    const model = this.hooks.getModel();
    if (this.tapModel !== model.id) {
      this.loadTaps();
      this.tapModel = model.id;
    }
    // BEFORE the popovers go: closing one destroys the row that may hold the focus, and a
    // capture taken afterwards has nothing left to record. The restore runs once the new
    // strips are in place, and lands on the trigger, since these three do not come back.
    const restorePopFocus = this.captureFocus();
    this.closeTapPop();
    this.closeSendPan();
    this.closeTypePop();
    this.host.classList.toggle("midi-learn", this.hooks.midi?.learnActive() ?? false);
    // A render replaces every strip element, so the transient state those elements
    // carried goes with them: the live meters' ballistics and keyboard focus. During
    // Live sync this path runs on every device-side edit that needs a read-back (and on
    // its idle safety net), so dropping that state reads as the console blinking under
    // the operator's hands. Carry it across the rebuild — the old refs stay in hand
    // until the new ones are built. The strip rack's scroll offset carries itself: the
    // clear and the refill are one task, so the empty rack is never laid out and the
    // offset is never clipped. Saving and rewriting it around the rebuild instead reads
    // and writes scrollLeft against a dirty tree, which forces a synchronous layout of
    // the whole rack — ~25 ms per render on WKWebView against ~6 ms without, on the path
    // Live sync takes for every device read-back reflect.
    const prev = this.refs;
    // The rack's own carry-over. Both restores run below; the popover one was taken first
    // and answers null unless the focus was inside a popover, so they cannot fight.
    const restoreFocus = this.captureFocus();
    this.ifxCensus = insertFxCensus(model, this.hooks.getPlan());
    this.refs = new Map();
    const { groups, master } = this.stripModels();
    this.stripsHost.replaceChildren();
    for (const g of groups) {
      const group = el("div", "con-group");
      const lbl = el("div", "con-grouplabel");
      lbl.textContent = g.label;
      group.append(lbl, ...g.ids.map((id) => this.buildStrip(this.toStripModel(id))));
      this.stripsHost.append(group);
    }
    if (master) {
      const group = el("div", "con-group master");
      const lbl = el("div", "con-grouplabel");
      lbl.textContent = t().console.master;
      group.append(lbl, this.buildStrip(this.toStripModel(master)));
      this.stripsHost.append(group);
    }
    // Lock every head (name / chips / knobs) to the tallest strip, so the head area
    // is uniform across all channels; the fader/meter zone (flex: 1) takes the rest
    // of the window height (the SENDS rack between them has its own fixed height).
    this.host.style.setProperty("--head-h", this.mainHeadHeight() + "px");
    for (const [id, r] of this.refs) this.carryMeterState(prev.get(id), r);
    // The popover one first: it answers null unless the focus WAS inside a popover, and in
    // that case the rack capture — taken after the close — has nothing of its own to say.
    // Neither is handed on across a replaced plan: the control the focus stood on belongs to
    // the plan that is gone, so a key still held there reaches nothing in the one that took
    // its place.
    const plan = this.hooks.getPlan();
    if (plan === this.builtFor) {
      restorePopFocus();
      restoreFocus();
    }
    this.builtFor = plan;
    this.syncCues();
    this.startMeters(); // rescope the meter subscription to the rebuilt strips
    this.redrawMeters();
  }

  // Carry a strip's live meter ballistics over from the elements it is replacing, so
  // a rebuild doesn't drop the bars to the floor and re-attack. Only when the strip
  // still meters the same tap — the tap objects are the table's own singletons, so
  // identity means the same addresses and the same lane count. The last-written
  // trackers (lv/lpk/lov/lmtr) stay at their fresh sentinels: the new elements are
  // undrawn, so the next paint must write them, not skip them as unchanged.
  private carryMeterState(from: StripRef | undefined, to: StripRef | undefined): void {
    if (!from || !to || from.tap !== to.tap) return;
    to.lanes.forEach((ln, i) => {
      const o = from.lanes[i];
      if (!o) return;
      ln.v = o.v;
      ln.pk = o.pk;
      ln.over = o.over;
    });
  }

  // Where keyboard focus sits inside the strips, as (strip id, the control's own identity).
  // The scroll offset is deliberately left out: the rack's is not restored (see
  // preserveFocus), only focus is.
  /**
   * Carry the keyboard's place across a rebuild of the strips.
   *
   * An ordinary control is keyed by the identity it is built with (`data-ctl` — its MIDI id
   * where it has one, else a fixed name such as the opener's kind), and the restore looks
   * for that identity among the controls the rebuilt strip offers to the keyboard. So a
   * control is found wherever the rebuild put it, and one the rebuild took away or turned
   * read-only answers nothing: the focus is dropped rather than handed to whatever moved
   * into its place — a +48V that HI-Z just locked does not pass the focus to HPF beside it.
   * A control built without an identity falls back to its position in the strip's tab
   * order plus its class.
   *
   * Two places the focus can be are not in that order at all, and both are ones this view
   * puts it in itself: the INS FX face where a sample rate has dropped the disclosure, and
   * the strip root under it. They are `tabindex="-1"` — present, not tabbable — so
   * `focusables` does not list them and an index key comes back empty, which drops the
   * focus to <body> on the next rebuild. A device-follow `refreshStrip` on the same channel
   * is enough to do it, so the state this view hands the operator would not survive the
   * next thing the unit says. They are keyed by NAME instead, which is also what makes the
   * restore meaningful: the face is the face whatever else the rebuild moved.
   */
  /**
   * The three popovers, each with the element it hangs off and the node it belongs to.
   * One table so the focus rules below cannot know about two of them and miss the third.
   */
  /** The chips the type popover currently hangs off, disclosure first — the order every
   *  landing here uses. ONE seat because two sites ask it: the focus table below and the
   *  release in `closeTypePop`. Written out at both, only one of them was widened when the
   *  popover began serving a second selector, and a whole-rack repaint then dropped focus to
   *  the document body — an FX strip carries neither INS FX class. */
  private get typePopSel(): readonly string[] {
    return this.typePopKind === "fx" ? [".con-fxopen"] : [".con-ifxopen", ".con-ifxface"];
  }

  private get popovers(): ReadonlyArray<{
    kind: "tap" | "pan" | "ifx";
    box: HTMLElement;
    openFor: string | null;
    sel: readonly string[];
  }> {
    return [
      { kind: "tap", box: this.tapPop, openFor: this.tapOpenFor, sel: [".con-tap"] },
      { kind: "pan", box: this.sendPanPop, openFor: this.sendPanOpenFor, sel: [".con-panbtn"] },
      { kind: "ifx", box: this.typePop, openFor: this.typePopFor, sel: this.typePopSel },
    ];
  }

  /** Focus standing inside an open popover, recorded as the row it is on AND the strip the
   *  popover belongs to — the two answers a rebuild can need, since a path may re-open the
   *  popover (the row is still there) or close it (only the trigger is). The row is keyed
   *  by its identity as a strip control is, and by its position only where it has none. */
  private popoverMark(active: HTMLElement): PopoverFocusMark | null {
    for (const p of this.popovers) {
      if (p.openFor === null || !p.box.contains(active)) continue;
      return {
        pop: p.kind,
        id: p.openFor,
        idx: focusables(p.box).indexOf(active),
        ctl: active.dataset.ctl,
        sel: p.sel,
      };
    }
    return null;
  }

  /** …and where it goes afterwards, decided by what the rebuild actually did rather than by
   *  which path called: the same row where the popover is open again and still offers it to
   *  the keyboard, the trigger on the rebuilt strip otherwise — a SEND PAN knob a lock just
   *  turned read-only hands the focus to the PAN button, not to the knob beside it. */
  private restorePopoverFocus(mark: PopoverFocusMark): HTMLElement | null {
    const p = this.popovers.find((x) => x.kind === mark.pop);
    if (p && p.openFor !== null) {
      const rows = focusables(p.box);
      const row = mark.ctl !== undefined ? rows.find((r) => r.dataset.ctl === mark.ctl) : rows[mark.idx];
      if (row) return row;
    }
    const root = this.refs.get(mark.id)?.root;
    // Each selector in TURN, not as one list: `querySelector` answers in document order, so
    // a list would hand back the INS FX face — which comes first — where the disclosure
    // beside it is the trigger that was pressed.
    for (const sel of mark.sel) {
      const hit = root?.querySelector<HTMLElement>(sel);
      if (hit) return hit;
    }
    return null;
  }

  private captureFocus(): () => void {
    return preserveFocus(
      // The whole console, not the strip rack: the popovers float outside the rack, and a
      // rebuild destroys what is inside them just as surely — `render` by closing all
      // three, `refreshStrip` by re-opening two of them against the fresh strip. Keyed
      // against the rack alone, focus inside a popover was not recorded at all, and the
      // rebuild dropped it to <body>.
      this.host,
      (active) => {
        const pop = this.popoverMark(active);
        if (pop) return pop;
        for (const [id, r] of this.refs) {
          if (!r.root.contains(active)) continue;
          if (active === r.root) return { id, anchor: "strip-root" as const };
          if (active.classList.contains("con-ifxface")) return { id, anchor: "ifx-face" as const };
          const ctl = active.dataset.ctl;
          if (ctl !== undefined) return { id, ctl };
          const idx = focusables(r.root).indexOf(active);
          return idx < 0 ? null : { id, idx, cls: active.className };
        }
        return null;
      },
      (mark) => {
        if ("pop" in mark) return this.restorePopoverFocus(mark);
        const root = this.refs.get(mark.id)?.root;
        if (!root) return null;
        if ("anchor" in mark) {
          // The face if it is still there, and the root under it — the same order the
          // close path uses, so a rebuild that changed which of them exists lands where a
          // fresh close would have.
          return mark.anchor === "ifx-face" ? (root.querySelector<HTMLElement>(".con-ifxface") ?? root) : root;
        }
        if ("ctl" in mark) return focusables(root).find((c) => c.dataset.ctl === mark.ctl);
        const target = focusables(root)[mark.idx];
        return target?.className === mark.cls ? target : null;
      },
    );
  }

  // Draw the meters onto freshly built elements in the same task as the rebuild. They
  // start undrawn — bars at the floor, readout "—" — and the paint loop would not
  // write the numeric readout until its next throttled frame (~1/6 s), so the reset
  // state would be on screen until then. A device-side sweep rebuilds the strip faster
  // than that (the follow reflect runs at up to 20 Hz), which is exactly the readout
  // flickering between "—" and the value. A strip whose tap has not streamed yet is
  // skipped and keeps its "—". `only` narrows the pass to the one strip a single-strip
  // rebuild replaced.
  private redrawMeters(only?: string): void {
    if (!this.live || !this.visible) return;
    const strips = only === undefined ? this.refs.values() : [this.refs.get(only)];
    for (const r of strips) if (r) this.paintStrip(r, true, false);
  }

  // The tallest head (a mono channel carries the most chips + two knobs) sets the
  // fixed head height for every strip. Measure it by laying out the strips off-screen
  // with auto-height heads, then cache by everything that changes what a head CONTAINS.
  //
  // Model + hidden set alone was not that. The chips a head carries also depend on each
  // channel's COMP/EQ type — an SSMCS channel had no GATE/COMP/EQ openers, so a plan
  // seeded in SSMCS measured a shorter head, and switching it back to COMP->EQ added a
  // chip row the cached height had no space for: the head then stayed clipped until
  // something rebuilt the view under a different key (a model switch, a hide/show, a
  // reload). The two banks now carry the same NUMBER of chips — the morphing strip's own
  // master and its one opener stand where COMP's and EQ's two openers stand — so the term
  // sits where the sample rate's does: kept for what a head carries rather than for a
  // height seen to move, since what balances it is a coincidence of counts rather than a
  // rule.
  //
  // The sample rate is in the key too, and it earns its place differently. Measured
  // 2026-08-14 over URX44V / URX44 / URX22 at 48 / 96 / 176.4 / 192 kHz, on each one's
  // default plan (17 / 17 / 15 heads), the rate moved no head in that set — chip count,
  // grid row count and element count come back identical at every rate. It does change
  // what a stereo channel's head CARRIES: above 96 kHz the EQ opener goes and the chip
  // beside it turns read-only. What absorbs that is the parity spacer, appended so a
  // group's last chip never stretches to full width — it takes the freed slot, and a
  // two-column group of 7-plus-spacer is the same number of rows as one of 8. So the term
  // is here for the shape of that compensation rather than for a height seen to move: it
  // costs one re-measure per rate change, and either rule changing without it is the
  // clipped head above, in silence.
  //
  // Each channel's input stage is in the key as well: the jack or source it reads decides
  // whether its head carries +48, Hi-Z and a gain knob at all — a channel with no source
  // carries none of them, and a stereo channel's is the one on the side it shows.
  private mainHeadHeight(): number {
    const plan = this.hooks.getPlan();
    const model = this.hooks.getModel();
    const types = model.nodes.map((n) => plan.nodeParams[n.id]?.compEqType).join(",");
    const stages = model.nodes
      .filter((n) => n.kind === "channel")
      .map((n) => {
        const s = shownStage(model, plan, n.id);
        return s.kind === "jack" ? `j${s.hiZ ? "h" : ""}` : s.kind[0];
      })
      .join("");
    const key = [model.id, [...plan.hidden].sort().join(","), plan.sampleRate, types, stages].join("|");
    if (this.headH.key === key) return this.headH.px;
    const savedRefs = this.refs;
    this.refs = new Map(); // buildStrip registers refs/listeners; keep them off the live map
    const probe = el("div", "con-strips");
    probe.style.cssText = "position:absolute;visibility:hidden;height:auto;";
    const { groups, master } = this.stripModels();
    for (const g of groups) for (const id of g.ids) probe.append(this.buildStrip(this.toStripModel(id)));
    if (master) probe.append(this.buildStrip(this.toStripModel(master)));
    this.host.append(probe);
    // Free every head from the inherited --head-h clamp first, then read them all,
    // so the heights collapse to content in one reflow instead of one write→read
    // thrash per head.
    const heads = [...probe.querySelectorAll<HTMLElement>(".con-head")];
    for (const h of heads) h.style.height = "auto";
    let max = 0;
    for (const h of heads) max = Math.max(max, h.offsetHeight);
    probe.remove();
    this.refs = savedRefs;
    this.headH = { key, px: max };
    return max;
  }

  private buildStrip(m: StripModel): HTMLElement {
    if (m.meterOnly) return this.buildMeterOnlyStrip(m);
    const model = this.hooks.getModel();
    const level = this.getMain(m);

    // A node whose master is off (CH_ON / MIX 675 / STEREO 582 / MONITOR 723, all on
    // np.on) is silenced whole — dim the strip like the graph does (shared predicate),
    // with the scribble power LED, not a badge, marking why. The MUTE chip below is a
    // separate control: the → STEREO send's ON/OFF, unaffected by the master.
    // …and a node the RATE removes is dimmed the same way, which is what the graph
    // already does with the same predicate. Every control it holds is read-only and stays
    // a MIDI-learn target, as is every send AIMED at it (`buildSendCol`): the unit is not
    // running the bus, and a value set on it would set nothing there.
    const rateOff = nodeRateDisabled(m.id, this.hooks.getPlan().sampleRate);
    const strip = el("div", "con-strip" + (m.inactive || rateOff ? " inactive" : ""));
    nameStrip(strip, m);
    // Programmatically focusable and out of the tab order: it is the floor a popover close
    // lands on when the control that opened it did not survive the rebuild.
    strip.tabIndex = -1;
    if (rateOff) strip.title = t().inspector.fx2RateLocked;
    strip.style.setProperty("--rail", m.rail);
    // A MONITOR strip is framed while it plays the CUE bus (syncCues).
    if (m.hasPhones) strip.dataset.cueMonitor = m.id;

    // head: scribble (with the power LED) + chips + gain (always the MAIN control set —
    // sends live in the SENDS rack below, so the head no longer swaps per send target).
    const head = el("div", "con-head");
    head.append(this.scribble(m));

    const cc = channelControl(model, m.id);
    // The jack or source this channel reads, which holds its input stage; a stereo channel's
    // on the side it shows.
    const stage = m.isChannel ? shownStage(model, this.hooks.getPlan(), m.id) : ({ kind: "none" } as const);
    const jack = stage.kind === "jack" ? stage : null;

    // Toggle chips in two 2-column groups: channel + input (HA) toggles, then the
    // processing chain GATE → COMP → EQ → INS FX. Each chip flips a plan flag (the
    // device mirrors it via the shared change funnel). An odd group gets an unused
    // spacer chip so the last real chip never stretches to full width.
    type BoolKey = "gateOn" | "compOn" | "eqOn" | "phase" | "phaseL" | "phaseR" | "hpf" | "cueInterrupt" | "mono";
    const planOf = (): NodeParams => this.hooks.getPlan().nodeParams[m.id] ?? {};
    const boolChip = (
      parent: HTMLElement,
      label: string,
      key: BoolKey,
      def: boolean,
      title?: string,
      spoken?: string,
    ): void => {
      this.makeChip(
        m.id,
        parent,
        label,
        false,
        planOf()[key] ?? def,
        () => {
          const next = !(planOf()[key] ?? def);
          this.nodeParamsOf(m.id)[key] = next;
          return next;
        },
        { midiId: controlId(m.id, key), title, keys: [key], spoken },
      );
    };

    // channel + input (HA) group
    const top = el("div", "con-chips");
    if (m.hasMute) {
      // The MUTE drives the fixed → STEREO send's ON/OFF (never its wire): a CH / FX →
      // STEREO assign (ships ON), or a MIX → STEREO "TO ST" (ships off). The node
      // master lives on the scribble power LED, a separate control.
      const mix = this.isMixBus(m.id);
      const conn = (): PlanConnection | undefined => sendConnection(this.hooks.getPlan(), m.id, MAIN_BUS);
      const sendOn = (): boolean => conn()?.params?.on ?? !mix; // assign ships ON, TO ST off
      this.makeChip(
        m.id,
        top,
        t().console.mute,
        true,
        !sendOn(),
        () => {
          const c = conn();
          const nextOn = !sendOn();
          if (c) c.params = { ...c.params, on: nextOn };
          return !nextOn; // chip "on" (highlighted) = muted
        },
        rateOff
          ? { midiId: controlId(m.id, "mute"), readonlyTitle: t().inspector.fx2RateLocked }
          : { midiId: controlId(m.id, "mute") },
      );
    }
    const cue = this.cueChip(m.id);
    if (cue) top.append(cue);
    // +48V and HI-Z of the jack this channel reads are never turned on together: while one
    // is on, the other's chip is read-only and says why; turning the lit one off stays
    // available. The value is the jack's, so either toggle re-renders every strip — each
    // channel reading that jack shows it. The read-only chip keeps its control id, so it can
    // still be MIDI-learned; the bound controller's ON is refused while the lock holds.
    const jackNp = (): NodeParams => (jack ? (this.hooks.getPlan().nodeParams[jack.node] ?? {}) : {});
    const inputChip = (label: string, key: InputSwitch): void => {
      if (!jack) return;
      const planKey = jackKey(key, jack.side);
      if (inputOnRefused(model.id, jack, jackNp(), key)) {
        this.makeChip(jack.node, top, label, false, false, () => false, {
          readonlyTitle: key === "phantom" ? t().inspector.phantomLockedByHiZ : t().inspector.hiZLockedByPhantom,
          midiId: controlId(m.id, key),
        });
        return;
      }
      const written: string[] = [planKey];
      this.makeChip(
        jack.node,
        top,
        label,
        false,
        Boolean(jackNp()[planKey]),
        () => {
          const next = !jackNp()[planKey];
          const patch = key === "hiZ" ? hiZPatch(jackNp(), jack.side, next) : { [planKey]: next };
          Object.assign(this.nodeParamsOf(jack.node), patch);
          written.splice(0, written.length, ...Object.keys(patch));
          return next;
        },
        { midiId: controlId(m.id, key), keys: written, rerender: true },
      );
    };
    // HA input toggles (+48 / polarity / HPF / Hi-Z). +48 and Hi-Z are the jack's.
    if (jack) inputChip("+48", "phantom");
    // Polarity: one Ф, announced by its word. A stereo channel's inverts the side it shows
    // (phaseL / phaseR).
    const phase = cc?.phases.find((ph) => ph.side === "" || ph.side === displayedSide(m.id));
    if (phase) boolChip(top, t().midi.param.phase, phase.key, false, undefined, t().midi.paramSpoken.phase);
    if (cc?.hasHpf) boolChip(top, "HPF", "hpf", false);
    if (jack?.hiZ) inputChip("Hi-Z", "hiZ");
    // MONITOR strips carry the device [CUE] (cue interrupt) and [MONO] buttons.
    // Both are confirmed device params (MONITOR_CUE_INTERRUPT / MONITOR_MONO), so
    // they sync live like the channel toggles. CUE Interrupt ships ON, MONO OFF.
    if (m.hasPhones) {
      boolChip(top, t().console.cue, "cueInterrupt", true, t().console.cueFull);
      boolChip(top, t().console.mono, "mono", false);
    }

    // processing group (GATE / COMP / EQ / INS FX / DUCKER)
    const proc = el("div", "con-chips");
    if (m.isMono) {
      // GATE keeps its chip (the ON toggle) and gains a narrow neighbour that
      // opens the tuning screen. A separate chip rather than a gesture on the
      // existing one: `wireActivate` binds click and Space/Enter with no `detail`
      // guard, so a double-click would toggle the gate twice and write twice, and
      // double-click is already the factory-value reset for the faders and knobs
      // here. It costs a slot in the two-per-row grid, so the processing chips
      // take a third row.
      boolChip(proc, "GATE", "gateOn", false);
      this.appendOpener(proc, this.dynOpenChip("gate", m.id));
      // Which COMP/EQ bank the channel runs decides which screen the COMP and EQ chips
      // open, and whether the SSMCS chip is there at all. Asked of channelDynamics
      // rather than re-derived here, so an opener cannot appear for a screen that
      // would refuse to open.
      const plan = this.hooks.getPlan();
      const dyn = channelDynamics(this.hooks.getModel(), m.id, plan.nodeParams[m.id]?.compEqType ?? COMP_EQ_COMP_FIRST);
      // The morphing bank's two on/offs live one level down in the plan, which boolChip's
      // flat writer cannot reach. One writer for both, so they cannot disagree about the
      // shape they patch or about how the edit reaches the undo differ.
      const ssmcsChip = (
        label: string,
        read: () => boolean,
        set: (cur: SsmcsParams, next: boolean) => SsmcsParams,
        // The field `set` writes, one level inside the bank. Named rather than derived:
        // `set` rebuilds the whole object, and naming the bank would claim every sibling
        // it copied — taking the device's answer for all of them.
        field: string,
        opts: { midiId?: string; title?: string },
      ): void => {
        this.makeChip(
          m.id,
          proc,
          label,
          false,
          read(),
          () => {
            const next = !read();
            const np = this.nodeParamsOf(m.id);
            np.ssmcs = set(np.ssmcs ?? {}, next);
            return next;
          },
          { ...opts, keys: [`ssmcs.${field}`] },
        );
      };
      // The morphing strip's own master, between GATE and COMP as the inspector orders
      // them and as the unit chains them.
      if (dyn && !dyn.comp) {
        ssmcsChip(
          "SSMCS",
          () => planOf().ssmcs?.on ?? SSMCS_INITIAL.on,
          (cur, next) => ({ ...cur, on: next }),
          "on",
          { midiId: controlId(m.id, "ssmcsOn") },
        );
        this.appendOpener(proc, this.dynOpenChip("ssmcs", m.id));
      }
      boolChip(proc, "COMP", "compOn", false);
      // The shipped COMP screen only. A morphing strip's COMP face is reached from the
      // SSMCS opener above and the face segment inside the screen, so the bank carries one
      // opener rather than one per face — and the COMP and EQ chips read here exactly as
      // they do on a channel with no strip at all.
      if (dyn?.comp) this.appendOpener(proc, this.dynOpenChip("comp", m.id));
      // The side-chain filter's own switch, between the two processors it sits between.
      // Every other on/off in this bank is reachable from the strip — SSMCS's master, the
      // compressor's, the EQ's — and this one was only inside the COMP screen. It writes
      // the same plan value and carries the same MIDI id as the row there, so the two are
      // one control in two places rather than two controls that agree.
      if (dyn && !dyn.comp) {
        ssmcsChip(
          "SC",
          () => planOf().ssmcs?.sc?.on ?? SSMCS_INITIAL.sc.on,
          (cur, next) => ({ ...cur, sc: { ...cur.sc, on: next } }),
          "sc",
          { midiId: controlId(m.id, "sideChain", SSMCS_SC_SCOPE), title: t().inspector.ssmcs.sideChain },
        );
      }
    }
    const rate = this.hooks.getPlan().sampleRate;
    if (m.hasEq) {
      // Stereo-channel EQ is inert at 176.4 / 192 kHz: show the chip forced off and
      // read-only (matches the inspector's locked EQ toggle), else a live toggle.
      const eqType = this.hooks.getPlan().nodeParams[m.id]?.compEqType ?? COMP_EQ_COMP_FIRST;
      if (channelEqUnavailable(m.id, rate)) {
        this.makeChip(m.id, proc, t().console.eq, false, false, () => false, {
          readonlyTitle: t().inspector.eqRateLocked,
          midiId: controlId(m.id, "eqOn"),
        });
        // No opener. A disclosure the rate has emptied is DROPPED rather than drawn inert:
        // a face carries the reason and stays, while a way IN to something that is not
        // there is nothing to show disabled. Same rule for the INS FX pair below.
        //
        // Its SLOT is kept, so the face beside it is the width it is at every other rate.
        if (hasEq(model, m.id, eqType)) this.appendOpener(proc, null);
      } else {
        boolChip(proc, t().console.eq, "eqOn", true);
        // The tuning screen's opener, as GATE and COMP have. Not offered in SSMCS mode,
        // where the EQ chip toggles the morphing strip's band section and the face segment
        // inside the SSMCS screen is what reaches its EQ face.
        if (hasEq(model, m.id, eqType)) this.appendOpener(proc, this.dynOpenChip("eq", m.id));
      }
    }
    if (insertFxControl(model, m.id)) {
      // The pair GATE / COMP / EQ use: a face and a disclosure sharing one row. The face
      // switches the insert in and out, the disclosure opens the type popover — and which
      // of the two the face IS depends on whether the strip holds an effect. Holding none,
      // there is no insert to switch, so the face opens the popover too rather than
      // choosing an effect on the operator's behalf. The type is an axis the other four
      // processors do not have, and it has to be settled before anything else means
      // anything: what a bypass switches out, and what a tuning screen would show.
      const menu = insertFxMenu(model, this.hooks.getPlan(), m.id, this.ifxCensus ?? undefined);
      // Every question below is asked of the value the DEVICE path will act on, not of the
      // raw plan value — the same answer the Inspector reads. A node's own control may not
      // carry what the plan holds, and translate turns such a value into No Effect and
      // writes neither the engine array nor the bypass, so a chip driven from the raw value
      // reports a strip as bypassing an effect that never reaches the unit.
      const eff = effectiveInsertFx(model, this.hooks.getPlan(), m.id);
      const holds = insertFxSelected({ insertFx: eff });
      // The lock the HELD effect carries, not the menu's: Pitch Fix stops at 48 kHz where
      // the amps and companders reach 96, so a strip holding it at 88.2 kHz is off while
      // the menu it came from still offers effects that run.
      const { locked: rateLocked, entry: selected } = insertFxRateLock(menu, eff);
      // Nothing to choose and nothing to release: this strip holds none, and every option
      // it could take is above the rate's ceiling. Not the same as a full slot table, which
      // still has a menu worth opening — and not the same as a strip HOLDING an effect the
      // rate forced off, whose menu is how its device-wide slot is given back.
      const free = insertFxFree(menu);
      const noneAtThisRate = !holds && !free.length && rateLocked;
      if (!holds)
        proc.append(
          this.insFxVacantChip(
            m.id,
            // Only where nothing at all can be taken, and only where ONE sentence is true
            // of the whole menu: every entry above its ceiling, or every entry held by
            // another node. A menu refused for two reasons at once — five entries the
            // STEREO link refuses beside two a slot holds, or Pitch Fix over its ceiling
            // beside amps another strip is holding — has no such sentence, and each of the
            // two that fit says something false about the other's entries. It gets none:
            // the face still opens, and the popover gives every row its own reason.
            free.length
              ? undefined
              : rateLocked
                ? t().inspector.insFxRateLocked
                : menu.every((e) => e.option.value === INSERT_FX_NONE || e.lock === "slot")
                  ? t().inspector.insFxSlotLocked
                  : undefined,
            noneAtThisRate,
          ),
        );
      else if (rateLocked)
        proc.append(
          this.buildChip(m.id, "INS FX", false, () => false, {
            cls: IFX_FACE_CLS,
            ctl: "ifx-face",
            midiId: controlId(m.id, "insertFxOn"),
            readonlyTitle:
              selected?.option.maxRate !== undefined
                ? t().inspector.insFxRateLockedAt(selected.option.label, formatRate(selected.option.maxRate))
                : t().inspector.insFxRateLocked,
          }),
        );
      else
        proc.append(
          this.buildChip(
            m.id,
            "INS FX",
            insertFxEngaged({ insertFx: eff, insertFxOn: planOf().insertFxOn }),
            () => this.toggleInsFxBypass(m.id),
            // Selection belongs to the popover and the bypass to this face, so the face
            // writes the bypass alone and the strip it sits on is the only thing that
            // changes. Nothing else shows the value, so there is nothing to keep in step.
            { cls: IFX_FACE_CLS, keys: ["insertFxOn"], midiId: controlId(m.id, "insertFxOn"), ctl: "ifx-face" },
          ),
        );
      // Dropped where the popover behind it can do NOTHING: a strip holding nothing at a
      // rate above every ceiling, where the one row is the No Effect it already is. A strip
      // HOLDING an effect the rate forced off keeps it — choosing No Effect there is how the
      // device-wide slot it still claims is given back, and that is the state an operator
      // most needs to reach it from.
      this.appendOpener(proc, noneAtThisRate ? null : this.insFxOpenChip(m.id, holds));
    }
    if (this.isFxChannel(m.id)) {
      // The same face + disclosure pair GATE / COMP / EQ / INS FX take. The face is drawn lit
      // and cannot be pressed — an FX channel's effect has no switch of its own beside the
      // strip's [ON], and it is always in — and its tooltip says so. The disclosure opens the
      // EFFECT TYPE popover.
      //
      // The chip is called EFFECT and not FX: the strip's own scribble already reads FX 1 /
      // FX 2, so a chip repeating that names nothing, and what a chip in this row names
      // everywhere else is WHAT HAPPENS there.
      //
      // An FX channel has no empty state to draw: its selector has no "none" value and the
      // factory type is a real effect, so the disclosure always opens onto something.
      proc.append(this.fxEffectFaceChip());
      this.appendOpener(proc, this.fxTypeOpenChip(m.id));
    }
    // DUCKER: the sidechain ducker hung under a stereo channel (its own node).
    // A shelved ducker drops its chip even while the parent strip stays.
    const hidden = this.hooks.getPlan().hidden;
    const duckerId = model.nodes.find((n) => n.kind === "ducker" && n.attachTo === m.id && !hidden.includes(n.id))?.id;
    if (duckerId) {
      const duckOn = (): boolean => this.hooks.getPlan().nodeParams[duckerId]?.duckerOn === true;
      this.makeChip(
        duckerId,
        proc,
        "DUCKER",
        false,
        duckOn(),
        () => {
          const next = !duckOn();
          this.nodeParamsOf(duckerId).duckerOn = next;
          return next;
        },
        { midiId: controlId(duckerId, "duckerOn"), keys: ["duckerOn"] },
      );
      // The tuning screen's opener, as GATE / COMP / EQ have. It carries the DUCKER
      // NODE's id, not the strip's: the chip lives here because a hung node has no
      // strip of its own, but the screen opens on the ducker.
      this.appendOpener(proc, this.dynOpenChip("ducker", duckerId));
    }

    for (const group of [top, proc]) {
      if (group.childElementCount % 2 === 1) group.append(el("div", "con-chip spacer"));
      if (group.childElementCount) head.append(group);
    }

    // A.GAIN of the jack, or D.GAIN of the source, this channel reads; none with no source.
    if (stage.kind !== "none") {
      const analog = stage.kind === "jack";
      const holder = stage.node;
      const key = analog ? jackKey("gain", stage.side) : "dGain";
      const range = analog
        ? jackGainRange(model.id, stage, this.hooks.getPlan().nodeParams[holder])!
        : { minDb: D_GAIN_MIN_DB, maxDb: D_GAIN_MAX_DB };
      const fromFactory = (this.factoryPlan().nodeParams[holder] as Record<string, unknown> | undefined)?.[key];
      const factory = typeof fromFactory === "number" ? fromFactory : range.minDb;
      const held = (): number | undefined => {
        const v = (this.hooks.getPlan().nodeParams[holder] as Record<string, unknown> | undefined)?.[key];
        return typeof v === "number" ? v : undefined;
      };
      // Horizontal-marking values: A.Gain +8/+55, D.Gain -14/+15.
      const [hl, hr] = analog ? [8, 55] : [-14, 15];
      const knob = this.addKnob(
        head,
        analog ? "A.GAIN" : "D.GAIN",
        {
          get: () => held() ?? factory,
          set: (v) => void ((this.nodeParamsOf(holder) as Record<string, unknown>)[key] = v),
          keys: [key],
          pairs: "own",
          min: range.minDb,
          max: range.maxDb,
          step: 1,
          format: (v) => (v > 0 ? "+" : "") + v,
          reset: factory,
          angle: (v) => -90 + ((v - hl) / (hr - hl)) * 180,
        },
        holder,
        controlId(m.id, "gain"),
      );
      // The knob's notes, on its tooltip and its description: the other channels reading the
      // same jack or source, and a device read that could not read it (the plan's value shows).
      const notes: string[] = [];
      const share = inputShare(model, this.hooks.getPlan(), m.id);
      if (share) notes.push(t().console.gainShared(share.others));
      if (this.hooks.getPlan().unreadNodes?.has(holder)) {
        const badge = el("span", "con-unread");
        badge.textContent = "?";
        badge.setAttribute("aria-hidden", "true");
        knob.append(badge);
        notes.push(t().inspector.notReadFromDevice);
      }
      if (notes.length) setTip(knob, [tipOf(knob), ...notes].filter(Boolean).join("\n"), true);
      // The jack's Clip Safe holding the gain down, shown on the gain it holds.
      const jack = analog ? jackIndex(stage) : null;
      const box = knob.parentElement;
      if (jack !== null && box) {
        box.dataset.clipSafeJack = String(jack);
        this.paintClipSafe(box);
      }
    }
    // PAN (mono) / BALANCE (stereo) = the source's → STEREO main-path pan,
    // L63 – C – R63. Per-send pan lives in the SENDS rack's SEND PAN popover.
    if (m.isChannel || this.isFxChannel(m.id)) {
      this.addSendPanKnob(
        head,
        m.id,
        MAIN_BUS,
        m.isBalance ? "BAL" : "PAN",
        rateOff ? t().inspector.fx2RateLocked : undefined,
        rateOff,
      );
    }
    // Master balance (STEREO 583 / MIX 676) = the bus output's L/R balance, edited
    // on the node's own `pan`. `busBalance` is the single source of truth for which
    // buses have one (shared with the inspector / translate / readback). The device
    // keeps the BALANCE label even under Pan Link (confirmed on URX44V), so it is
    // always "BAL".
    if (busBalance(m.id)) {
      this.addNodePanKnob(head, m.id, "BAL");
    }
    if (m.hasPhones) {
      // PHONES output level: a 0.0..10.0 scale (not dB) on the monitor bus,
      // independent of the monitor fader (PHONES 1 ↔ mon1, PHONES 2 ↔ mon2).
      const factory = this.factoryPlan().nodeParams[m.id]?.phonesLevel ?? PHONES_LEVEL_DEFAULT;
      this.addKnob(
        head,
        "PHONES",
        {
          get: () => this.hooks.getPlan().nodeParams[m.id]?.phonesLevel ?? PHONES_LEVEL_DEFAULT,
          set: (v) => void (this.nodeParamsOf(m.id).phonesLevel = v),
          keys: ["phonesLevel"],
          min: PHONES_LEVEL_MIN,
          max: PHONES_LEVEL_MAX,
          step: 0.1,
          format: (v) => v.toFixed(1),
          reset: factory,
          // 2.0 at the left horizontal, 8.0 at the right (the device's markings).
          angle: (v) => -90 + ((v - 2) / (8 - 2)) * 180,
        },
        m.id,
        controlId(m.id, "phonesLevel"),
      );
    }
    strip.append(head);

    // SENDS rack: per-strip columns for every MIX/FX send (enable chip + PRE button +
    // vertical mini-fader), a header (label / value readout / global collapse), and a
    // SEND PAN popover trigger. Built between the head and the fader zone, at a fixed
    // height so the fader tops stay aligned across every strip (blank on sendless ones).
    const rack = this.buildSendRack(m);

    // fader zone: a meter-point header row, then the fader (thin slot + cap;
    // position = setting) beside the dB scale and the live level meter.
    const zone = el("div", "con-faderzone");
    const tapKey = this.tapKeyOf(m.id);
    const tapHead = el("div", "con-taphead");
    if (tapsFor(m.id, this.hooks.getModel().id).length > 1) tapHead.append(this.buildTapBadge(m.id));
    zone.append(tapHead);
    const zrow = el("div", "con-zrow");

    const fader = el("div", "con-fader");
    fader.setAttribute("role", "slider");
    fader.setAttribute("aria-label", spoken(m.label));
    fader.dataset.ctl = controlId(m.id, "level");
    setLevelRange(fader, m.range);
    fader.tabIndex = 0;
    const track = el("div", "track");
    // The 0 dB line rides the fader (not the inset track) so it shares the cap's
    // coordinate space and passes through the cap centre when the fader sits at 0 dB.
    const zero = el("div", "zero");
    zero.style.setProperty("--zero", (1 - dbToFrac(0, m.range)) * 100 + "%");
    const cap = el("div", "cap");
    fader.append(track, zero, cap);

    // Meter column: the ladder shares the fader ruler, topping out at the 0 dB mark
    // with the OVER clip window above it. Stereo taps split into independent L/R bars.
    const tap = tapFor(m.id, tapKey, this.hooks.getModel().id) ?? null;
    const { meter, lanes } = this.buildMeterColumn(m.range, isStereoTap(tap));

    zrow.append(fader, this.buildScale(m.range), meter);
    zone.append(zrow);
    strip.append(rack.el, zone);

    // readout: fader set-level dB (white, FADER) and, for metered strips, the live
    // meter value of the selected tap (amber, METER). The captions and colour tell
    // the static set level from the live meter apart.
    const readout = el("div", "con-readout");
    const faderCell = el("div", "rd");
    const dbEl = el("div", "rv");
    faderCell.append(readCap(t().console.readFader), dbEl);
    readout.append(faderCell);
    // Only metered strips build the meter readout cell; a fader-only strip keeps a
    // bare detached value element as its readMtr (never appended, never updated).
    let mtrEl: HTMLElement;
    if (hasMeter(m.id, this.hooks.getModel().id)) {
      const rc = meterReadCell();
      readout.append(rc.cell);
      mtrEl = rc.value;
    } else {
      mtrEl = el("div", "rv");
    }
    strip.append(readout);

    const refObj: StripRef = {
      m,
      root: strip,
      lanes,
      cap,
      fader,
      readDb: dbEl,
      readMtr: mtrEl,
      tap,
      sig: { lmtr: 1 },
      sendCols: rack.cols,
    };
    this.refs.set(m.id, refObj);
    // Paint the cap position, readout and aria-valuenow through the same helper
    // the edit path uses (as buildSendCol does via updateColLevel), so build and
    // edit cannot drift apart.
    this.updateStripLevel(refObj, level);
    this.wireFader(refObj, rateOff ? t().inspector.fx2RateLocked : undefined);
    return strip;
  }

  private wireFader(r: StripRef, readonlyTitle?: string): void {
    const fader = r.fader;
    if (!fader) return; // meter-only strips have no fader to wire
    const range = r.m.range;
    const midiId = controlId(r.m.id, "level");
    // A strip the rate removes paints its level and takes no edit, and stays a learn target.
    if (readonlyTitle) {
      fader.classList.add("readonly");
      fader.setAttribute("aria-disabled", "true");
      setTip(fader, readonlyTitle);
      this.learnOnly(fader, midiId);
      return;
    }
    this.midiMark(fader, midiId);
    const setLevel = (db: number): void => {
      const written = this.setMain(r.m, db);
      this.updateStripLevel(r, db);
      this.commit(r.m.id, written);
      this.mirrorPartnerLevel(r.m.id); // a linked partner tracks the fader live
    };
    fader.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      if (this.midiArm(midiId)) return;
      const rect = fader.getBoundingClientRect();
      // The cap centre travels the element's whole height (`--pos` is a percentage of
      // it, under a -50% translate), so that is the scale a pointer maps through — not
      // the groove's 6 px inset, which used to be subtracted here and made the mapping
      // agree with the cap at mid-travel only, drifting to 1.3 detents by either end
      // (measured at the default window; 2.7 at the minimum one).
      const travel = rect.height;
      // A press that lands on the cap grabs it where it is and writes nothing: the cap
      // is 14 px on a travel worth a few pixels per detent, so jumping to the pointer
      // moved the level by up to 1.7 detents (3.6 at the minimum window) before the
      // operator had moved at all. A press on the bare track still jumps the cap under
      // the pointer, which is what an <input type="range"> does away from its thumb.
      // Either way the rest of the gesture is relative, so the cap tracks the pointer
      // 1:1 from wherever it started.
      const capBox = r.cap?.getBoundingClientRect();
      const onCap = !!capBox && e.clientY >= capBox.top && e.clientY <= capBox.bottom;
      const startFrac = onCap ? dbToFrac(this.getMain(r.m), range) : 1 - (e.clientY - rect.top) / travel;
      const startY = e.clientY;
      trackDrag(fader, e, (ev) => setLevel(fracToDb(startFrac + (startY - ev.clientY) / travel, range)), {
        seed: !onCap,
      });
    });
    fader.addEventListener("keydown", (e) => {
      if (this.midiLearnKey(e, midiId)) return;
      const next = this.faderKeyStep(e, range, this.getMain(r.m));
      if (next === null) return;
      e.preventDefault();
      setLevel(next);
    });
    // Double-click resets the fader to its factory value.
    fader.addEventListener("dblclick", () => {
      if (this.hooks.midi?.learnActive()) return; // pointerdown already armed
      setLevel(this.mainLevelOf(this.factoryPlan(), r.m));
    });
    // Hover + wheel steps one detent (mirrors the Arrow keys); skipped while
    // assigning MIDI so a stray scroll doesn't edit an armed control.
    onWheelStep(
      fader,
      (dir) => setLevel(this.faderWheelStep(range, this.getMain(r.m), dir)),
      () => this.hooks.midi?.learnActive(),
    );
  }

  private updateStripLevel(r: StripRef, db: number): void {
    if (!r.cap || !r.readDb || !r.fader) return; // meter-only strip has no fader
    const frac = dbToFrac(db, r.m.range);
    r.cap.style.setProperty("--pos", (1 - frac) * 100 + "%");
    const f = fmtDb(db, r.m.range);
    setLevelText(r.readDb, f.text);
    r.readDb.classList.toggle("off", f.off);
    r.fader.setAttribute("aria-valuenow", String(Math.round(db)));
    r.fader.setAttribute("aria-valuetext", levelValueText(db, r.m.range));
  }

  // ---- meters ----

  // Subscribe to the meters of the strips currently on screen. Safe to call on
  // every render: it self-guards on live/visible and only re-subscribes when the
  // displayed address set actually changes (e.g. a model switch), so mode/lang
  // re-renders don't churn the broker registration.
  // The awaited half of startMeters. Its failure has to be loud: bars stuck on
  // the floor look exactly like silence, and an operator reading them chases gain
  // that was never the problem — so it goes to the same handler a live error takes.
  private async resubscribeMeters(addrs: Array<[number, number]>, sig: string): Promise<void> {
    this.subPending = true;
    let unsub: () => void;
    try {
      unsub = await subscribeMeters(this.store, addrs);
    } catch (e) {
      this.subSig = "";
      this.hooks.onMeterError?.(e instanceof Error ? e.message : String(e));
      return;
    } finally {
      this.subPending = false;
    }
    // A stop/re-scope raced the registration; drop this one rather than leaving a
    // stream nothing will unsubscribe.
    if (this.subSig !== sig || !this.live || !this.visible) unsub();
    else this.unsub = unsub;
  }

  private startMeters(): void {
    if (!this.live || !this.visible || this.metersLent) return;
    const taps: MeterTap[] = [];
    for (const r of this.refs.values()) if (r.tap) taps.push(r.tap);
    const addrs = tapAddrs(taps);
    const sig = addrs.map((a) => a.join(":")).join(",");
    // subPending closes the window the await opens: without it a render landing
    // mid-registration sees no unsub, and re-registers the same address set —
    // which on the device is unregister-then-register for every address, the
    // ~1 s stall hide() already documents.
    //
    // It closes that window by DISCARDING the re-scope rather than queuing it, and
    // that is a known defect rather than a subtlety: pick tap A, then pick tap B
    // inside A's registration (up to ~1 s), and this returns without recording that
    // B is what is wanted. A resolves, `resubscribeMeters` keeps it because the
    // signature still matches, and the console then shows B while the broker streams
    // A — B's lane at the floor and its readout at "—" until some unrelated edit
    // forces a render. Bars stuck at the floor are the reading `onMeterError` exists
    // to prevent, and nothing fires here. It is pinned as-is by the race harness
    // (`meter-rescope-inside-subpending-ladder`, docs/{en,ja}/live-race-harness.md),
    // whose expectation is the defect, so a fix has to rewrite that case as a set.
    // What closes it is one line — re-invoking `startMeters()` at the end of
    // `resubscribeMeters`, which self-guards and no-ops when the kept signature
    // already matches. Left out of the 2026-08-13 audit's fixes deliberately, as the
    // one finding of the 51 triaged out; nothing has decided against it since.
    if (!this.subPending && (!this.unsub || sig !== this.subSig)) {
      this.unsub?.();
      this.unsub = null;
      this.subSig = sig;
      void this.resubscribeMeters(addrs, sig);
    }
    if (!this.raf) {
      // Cap repaints to ~30 fps for smooth ballistics (the device streams at ~10 Hz;
      // the extra frames interpolate the attack/release). Per-frame cost is kept low
      // by driving the bars with compositor-only transforms (scaleY / translateY, no
      // layout/paint) and throttling the numeric readout to a fraction of the rate.
      const FRAME_MS = 1000 / 30;
      let last = 0;
      const tick = (now: number): void => {
        if (now - last >= FRAME_MS) {
          last = now;
          this.paintMeters();
        }
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }
  }

  // Stop the paint loop without touching the broker subscription. Used when hiding
  // the view across a graph/console toggle so the warm stream survives (see hide()).
  private stopPaint(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private stopMeters(): void {
    this.stopPaint();
    if (this.unsub) {
      this.unsub();
      this.unsub = null;
    }
    this.subSig = "";
    this.store.clear();
    this.resetMeters();
  }

  // Drop every signal meter to its floor so disconnecting doesn't leave the bars
  // frozen at their last live reading.
  private resetMeters(): void {
    for (const r of this.refs.values()) {
      const s = r.sig;
      for (const ln of r.lanes) {
        ln.v = ln.pk = ln.over = 0;
        if (ln.lv !== 0) {
          ln.shade.style.setProperty("--lvl", "0");
          ln.lv = 0;
        }
        if (ln.lpk !== 0) {
          ln.peak.style.setProperty("--pk", "0");
          ln.lpk = 0;
        }
        if (ln.lov !== 0) {
          ln.clip.style.setProperty("--clip", "0");
          ln.lov = 0;
        }
        if (ln.live) {
          ln.col.classList.remove("live"); // release the compositor layers on teardown
          ln.live = false;
        }
      }
      if (s.lmtr !== 1) {
        r.readMtr.textContent = "—";
        r.readMtr.classList.remove("off");
        s.lmtr = 1;
      }
    }
  }

  private paintMeters(): void {
    // Refresh the numeric readout on only every READOUT_EVERY-th frame: its text
    // change relayouts/repaints the cell, so doing it every frame on every strip is
    // a needless per-frame cost the animated bars don't share.
    const showReadout = this.paintN++ % READOUT_EVERY === 0;
    for (const r of this.refs.values()) this.paintStrip(r, showReadout, true);
  }

  // One strip's meter. `step` advances the ballistics (the animation loop); a redraw
  // onto freshly built elements passes false, so a rebuild writes the state the meter
  // already holds instead of aging it — the animation keeps its own clock.
  private paintStrip(r: StripRef, showReadout: boolean, step: boolean): void {
    if (!r.tap) return;
    const reading = this.store.readingTap(r.tap);
    if (!reading) return;
    const s = r.sig;
    // Numeric meter readout (selected tap, peak of L/R), -∞ below the floor.
    const peakDb = Math.max(reading.l, reading.r);
    const mtr = peakDb <= METER_FLOOR_DB ? -999 : Math.round(peakDb * 10);
    if (showReadout && mtr !== s.lmtr) {
      setLevelText(r.readMtr, mtr === -999 ? "-∞" : (mtr / 10).toFixed(1));
      r.readMtr.classList.toggle("off", mtr === -999);
      s.lmtr = mtr;
    }
    // Drive each lane from its own channel — lane 0 = L, lane 1 = R. A mono strip has
    // only lane 0, and readingTap mirrors R onto L there, so lane 0 meters its true
    // level either way (no peak-fold; the peak-of-L/R fold is only for the readout).
    for (let i = 0; i < r.lanes.length; i++) {
      const ln = r.lanes[i];
      const chDb = i === 0 ? reading.l : reading.r;
      const chOver = i === 0 ? reading.overL : reading.overR;
      const target = meterFrac(chDb, r.m.range);
      if (step) {
        // Fast attack, slow release for a meter-like response; peak hold decays slowly.
        ln.v = target > ln.v ? target : ln.v + (target - ln.v) * 0.3;
        ln.pk = Math.max(ln.pk * 0.985, ln.v);
        // OVER clip cap: latch full on a clip in this channel, then fade so a brief
        // over lingers.
        ln.over = chOver ? 1 : ln.over * 0.95;
      }
      // Write only the values that actually changed (idle meters rest, so most
      // frames skip every write) — at integer-percent resolution.
      const v = Math.round(ln.v * 100);
      const pk = Math.round(ln.pk * 100);
      const over = ln.over > 0.02 ? Math.round(ln.over * 100) : 0;
      // --lvl / --pk are fractions (0..1) driving compositor-only transforms
      // (scaleY / translateY) on the shade and peak — no layout/paint per frame.
      if (v !== ln.lv) {
        ln.shade.style.setProperty("--lvl", v / 100 + "");
        ln.lv = v;
      }
      if (pk !== ln.lpk) {
        ln.peak.style.setProperty("--pk", pk / 100 + "");
        ln.lpk = pk;
      }
      if (over !== ln.lov) {
        ln.clip.style.setProperty("--clip", over / 100 + "");
        ln.lov = over;
      }
      // Promote the shade/peak to compositor layers (via `.live`) only while the lane
      // is actually animating; an idle lane (at the floor, no clip) drops its layers,
      // so a mostly-quiet console isn't compositing a layer per silent meter.
      const active = v > 0 || pk > 0 || over > 0;
      if (active !== ln.live) {
        ln.col.classList.toggle("live", active);
        ln.live = active;
      }
    }
  }

  // ---- level get/set on the plan ----

  private isFxChannel(id: string): boolean {
    return id === "bus.fx1" || id === "bus.fx2";
  }
  private isMixBus(id: string): boolean {
    return id === "bus.mix1" || id === "bus.mix2";
  }

  /** Whether a strip has a send connection to a target bus (a rack column exists).
   *  A strip never sends to itself, so `id === target` is excluded here once. */
  private hasSend(id: string, target: SendTarget): boolean {
    return id !== target && sendConnection(this.hooks.getPlan(), id, target) !== undefined;
  }

  /** Shared PAN/BALANCE knob spec (±63, C / Ln / Rn display); get/set/reset bind
   *  the source — a connection's send pan or a node's master balance. */
  private panKnobSpec(
    get: () => number,
    set: (v: number) => void,
    reset: number,
    readonlyTitle?: string,
    keys?: readonly string[],
    assignable = false,
  ): KnobSpec {
    return {
      assignable,
      get,
      set,
      min: PAN_MIN,
      max: PAN_MAX,
      step: 1,
      format: (v) => (v === 0 ? "C" : v < 0 ? "L" + -v : "R" + v),
      reset,
      readonlyTitle,
      keys,
      pairs: "pan",
    };
  }

  /** Add a PAN/BALANCE knob bound to a send connection's `pan` (L63 – C – R63),
   *  resetting to the factory plan's value on double-click. */
  private addSendPanKnob(
    head: HTMLElement,
    id: string,
    target: string,
    label: string,
    readonlyTitle?: string,
    assignable = false,
  ): void {
    const conn = (): PlanConnection | undefined => sendConnection(this.hooks.getPlan(), id, target);
    const factory = sendConnection(this.factoryPlan(), id, target)?.params?.pan ?? 0;
    this.addKnob(
      head,
      label,
      this.panKnobSpec(
        () => conn()?.params?.pan ?? 0,
        (v) => {
          const c = conn();
          if (c) c.params = { ...c.params, pan: v };
        },
        factory,
        readonlyTitle,
        undefined,
        assignable,
      ),
      id,
      controlId(id, "pan", target === MAIN_BUS ? undefined : target),
    );
  }

  /** Add a BALANCE/PAN knob bound to a bus node's own master balance (`pan`,
   *  STEREO 583 / MIX 676), resetting to the factory plan's value on double-click. */
  private addNodePanKnob(head: HTMLElement, id: string, label: string): void {
    const factory = this.factoryPlan().nodeParams[id]?.pan ?? 0;
    this.addKnob(
      head,
      label,
      this.panKnobSpec(
        () => this.hooks.getPlan().nodeParams[id]?.pan ?? 0,
        (v) => void (this.nodeParamsOf(id).pan = v),
        factory,
        undefined,
        ["pan"],
      ),
      id,
      controlId(id, "pan"),
    );
  }

  private nodeParamsOf(id: string): NodeParams {
    const plan = this.hooks.getPlan();
    return (plan.nodeParams[id] ??= {});
  }

  /** Apply a console edit to `id`: mirror it onto the linked partner — plus the insert
   *  FX, which the pair holds one of — then run the shared change funnel. Returns whether
   *  it mirrored (the caller rebuilds so the partner strip catches up). */
  private commit(id: string, written: readonly string[] = [], defaults: readonly string[] = []): boolean {
    const model = this.hooks.getModel();
    const plan = this.hooks.getPlan();
    const mirrored = mirrorLinkedPair(model, plan, id);
    const insFxMirrored = mirrorLinkedInsertFx(model, plan, id);
    // Each mirror names only what IT wrote, the same rule the inspector's funnel
    // follows: the pair mirror carries THIS edit's SHARED keys onto the partner, and the
    // insert-FX mirror the three-key pair state it copies whenever the pair is linked.
    // A key no mirror wrote — one the pair does not share included — stays the device's
    // to answer for.
    const keys = written.map((k) => nodeParamContestPath(id, k));
    const partner = partnerChannel(model, id);
    if (partner) {
      if (mirrored) for (const k of written) if (pairSharesNodeKey(k)) keys.push(nodeParamContestPath(partner, k));
      if (insFxMirrored) for (const k of INSERT_FX_PAIR_KEYS) keys.push(nodeParamContestPath(partner, k));
    }
    // A linked MIX's send pans from this strip (and a mirrored partner) follow its position.
    keys.push(...alignLinkedSendPans(plan, withLinkedPartner(model, plan, id)));
    // The insert-FX mirror copies the engine values, defaults included.
    const seeded = defaults.map((path) => [id, path] as const);
    if (partner && insFxMirrored) seeded.push(...defaults.map((path) => [partner, path] as const));
    this.hooks.onChange(keys, seeded);
    return mirrored || insFxMirrored;
  }

  /** Rebuild once after editing a STEREO-linked strip so the mirrored partner strip
   *  catches up — a live drag/keypress updates only the dragged strip. Used by the
   *  chips / knobs, where the partner's whole head may change. `pairs` names how the partner
   *  follows the edited value: where it did not move, the rebuild only takes down what is open
   *  on it — the SEND PAN popover, which `render()` closes where `refreshStrip` re-opens it. */
  private syncPartnerStrip(id: string, pairs?: "pan" | "own"): void {
    const model = this.hooks.getModel();
    const plan = this.hooks.getPlan();
    if (!isStereoLinkedPair(model, plan, id)) return;
    if (pairs === "own") return;
    if (pairs === "pan" && !isBalLinkedPair(model, plan, id)) return;
    this.render();
  }

  /** A gain knob that writes a jack or a source (`holder`) shows its value on every other
   *  strip reading that jack or source, in place, while it moves: each of those channels
   *  applies the one value. A strip on the other jack of the same MIC/LINE node — a stereo
   *  strip showing that jack included — and a knob on any other node, are left alone. */
  private mirrorHolderKnob(holder: string, knob: HTMLElement, val: HTMLElement, keys?: readonly string[]): void {
    const model = this.hooks.getModel();
    if (!model.nodes.some((n) => n.id === holder && n.kind === "input")) return;
    // A jack's gain names its side; a source's D.Gain has none.
    const side = keys?.find((k) => /^gain[LR]$/.test(k))?.slice(-1) as JackSide | undefined;
    for (const r of inputStageReaders(model, this.hooks.getPlan(), holder, side)) {
      // A stereo strip shows one side's knob; one showing the other jack is left alone.
      if (r.side !== undefined && r.side !== displayedSide(r.channel)) continue;
      const other = this.refs
        .get(r.channel)
        ?.root.querySelector<HTMLElement>(`.con-knob[data-ctl="${controlId(r.channel, "gain")}"]`);
      if (!other || other === knob) continue;
      other.style.setProperty("--rot", knob.style.getPropertyValue("--rot"));
      for (const a of ["aria-valuenow", "aria-valuetext"]) other.setAttribute(a, knob.getAttribute(a) ?? "");
      const otherVal = other.parentElement?.querySelector<HTMLElement>(".val");
      if (otherVal) otherVal.textContent = val.textContent;
    }
  }

  /** After a send pan turned under Pan Link moved its strip, repaint from the plan the knobs the
   *  turn moved: the strip's own PAN and a linked partner's, and every send pan in the open
   *  SEND PAN popover. */
  private repaintPanLinked(strip: string): void {
    const plan = this.hooks.getPlan();
    for (const id of withLinkedPartner(this.hooks.getModel(), plan, strip)) {
      const own = this.refs.get(id)?.root.querySelector<HTMLElement>(`.con-knob[data-ctl="${controlId(id, "pan")}"]`);
      if (own) this.knobRepaint.get(own)?.();
    }
    for (const knob of this.sendPanPop.querySelectorAll<HTMLElement>(".con-knob")) this.knobRepaint.get(knob)?.();
  }

  /** Push a STEREO-linked strip's mirrored fader level onto the partner strip's level
   *  DOM in place, so a linked fader tracks live without a rebuild (keeps focus). */
  private mirrorPartnerLevel(id: string): void {
    if (!isStereoLinkedPair(this.hooks.getModel(), this.hooks.getPlan(), id)) return;
    const partner = partnerChannel(this.hooks.getModel(), id);
    const pr = partner ? this.refs.get(partner) : undefined;
    if (!pr) return;
    this.updateStripLevel(pr, this.getMain(pr.m));
  }

  /** Mirror a STEREO-linked strip's send-column fader onto the partner strip's matching
   *  column DOM in place, so a linked send fader tracks live without a rebuild. */
  private mirrorPartnerSend(id: string, target: SendTarget): void {
    if (!isStereoLinkedPair(this.hooks.getModel(), this.hooks.getPlan(), id)) return;
    const partner = partnerChannel(this.hooks.getModel(), id);
    const pr = partner ? this.refs.get(partner) : undefined;
    const col = pr?.sendCols?.find((c) => c.target === target);
    if (!col) return;
    const pc = sendConnection(this.hooks.getPlan(), partner!, target);
    this.updateColLevel(col, pr!.m.range, pc?.params?.level ?? LEVEL_OFF_DB, pc?.params?.tap === "pre");
  }

  // The factory plan (cached): the source for double-click "reset to default".
  private factoryPlan(): Plan {
    const id = this.hooks.getModel().id;
    if (!this.factory || this.factory.id !== id) this.factory = { id, plan: defaultPlan(id) };
    return this.factory.plan;
  }

  private mainLevelOf(plan: Plan, m: StripModel): number {
    if (m.isOsc) return plan.nodeParams[m.id]?.osc?.level ?? -14;
    if (m.fadersOnly) return plan.nodeParams[m.id]?.level ?? 0;
    // channel / FX channel main path = the fixed send into STEREO
    return sendConnection(plan, m.id, MAIN_BUS)?.params?.level ?? 0;
  }

  private getMain(m: StripModel): number {
    return this.mainLevelOf(this.hooks.getPlan(), m);
  }

  /** Returns the node-parameter keys it wrote, for the change funnel's write witness.
   *  Empty where the main path is a wire's level rather than a node's own. */
  private setMain(m: StripModel, db: number): readonly string[] {
    const plan = this.hooks.getPlan();
    if (m.isOsc) {
      const np = this.nodeParamsOf(m.id);
      np.osc = { ...np.osc, level: db };
      return ["osc.level"];
    }
    if (m.fadersOnly) {
      this.nodeParamsOf(m.id).level = db;
      return ["level"];
    }
    const conn = sendConnection(plan, m.id, MAIN_BUS);
    if (conn) conn.params = { ...conn.params, level: db };
    return [];
  }

  // The factory send level (double-click reset); the live send level/pan/tap are read
  // off the column's captured connection object (see buildSendCol), not via a helper.
  private sendLevelOf(plan: Plan, id: string, target: SendTarget): number {
    return sendConnection(plan, id, target)?.params?.level ?? LEVEL_OFF_DB;
  }

  // The INS FX face drives the device's insert ON/OFF (bypass) and does not touch the
  // selection: absent reads as on, matching the device's auto-engage on a selector write.
  // Returns the new on state. Reached only where the strip holds an effect the device path
  // will act on — the callers put the vacant face and the rate-stopped one in front of it.
  private toggleInsFxBypass(id: string): boolean {
    const np = this.nodeParamsOf(id);
    np.insertFxOn = np.insertFxOn === false;
    return np.insertFxOn;
  }

  /**
   * Select an insert effect on a strip, or release the one it holds.
   *
   * The outgoing family's engine values are parked under their own family first: a bare
   * slot number left behind reads as the NEW family's, where the same slot is a different
   * parameter under a different law. Selecting engages the insert, which is what the unit
   * does on a selector write; releasing writes the intent off rather than leaving a
   * dormant switch to be picked up by whatever is selected next.
   *
   * A selection reaches every other strip — each effect is one device-wide slot — so this
   * rebuilds the view rather than the chip, and puts focus back on the disclosure the
   * press came from. The popover is gone by then; without this the focus is on the body
   * and the keyboard has lost its place in the rack.
   *
   * Choosing an effect then OPENS its screen. Picking a type is not the end of anything —
   * what the operator came for is the effect's own values, and every one of them is on the
   * screen — so the second press this used to need was a press that had one destination.
   * Releasing is the opposite and opens nothing: No Effect is the way out, and a screen
   * over a strip that now holds nothing has nothing to show.
   */
  private setInsFx(id: string, value: number): void {
    const np = this.nodeParamsOf(id);
    const parked = parkOutgoingInsertFxParams(np);
    // The plan takes the selected type's defaults for every slot it does not hold, which is
    // what the screen shows and what the unit fills the engine with.
    const { params, seeded } = seedInsertFxParams(parked ?? undefined, value);
    if (parked || seeded.length > 0) np.insertFxParams = params;
    np.insertFx = value;
    np.insertFxOn = value !== INSERT_FX_NONE;
    this.closeTypePop();
    // Only what this edit wrote. The engine values are named when there were some to
    // park and not otherwise — naming that group on a selection that moved nothing in it
    // would take the device's answer for every slot the re-key merely copied.
    this.commit(
      id,
      parked ? INSERT_FX_PAIR_KEYS : ["insertFx", "insertFxOn"],
      seeded.map((key) => `insertFxParams.${key}`),
    );
    this.render();
    // Asked of the same function the launcher beside the list asks, so the press that
    // chooses and the press that opens cannot disagree about whether there is a screen.
    if (value !== INSERT_FX_NONE && insertFxScreenFamily(this.hooks.getModel(), this.hooks.getPlan(), id) !== null) {
      this.hooks.onOpenDynScreen?.("insfx", id);
      return;
    }
    this.focusInsFxAnchor(id);
  }

  /**
   * Focus the control on the rack that opens `kind`'s tuning screen for `id`, as the rack is
   * drawn now — the screen asks for it when it closes, since a strip rebuilt while it was open
   * no longer holds the element that opened it, and the INS FX and FX popovers close (and
   * take their rows with them) before the screen opens.
   *
   * The INS FX and FX screens are reached through their strip's disclosure, which is where a
   * selection lands too. The SSMCS bank's faces share the one opener beside the SSMCS chip.
   */
  focusOpener(kind: DynKind, id: string): void {
    const root = this.refs.get(id)?.root;
    let target: HTMLElement | null | undefined;
    if (kind === "insfx") {
      target = root?.querySelector<HTMLElement>(".con-ifxopen") ?? root?.querySelector<HTMLElement>(".con-ifxface");
    } else if (kind === "fx") {
      target = root?.querySelector<HTMLElement>(".con-fxopen");
    } else {
      const opens = kind === "ssmcsComp" || kind === "ssmcsEq" ? "ssmcs" : kind;
      target = [...this.host.querySelectorAll<HTMLElement>(".con-chip-open[data-dyn-open]")].find(
        (c) => c.dataset.dynOpen === opens && c.dataset.dynNode === id,
      );
    }
    target?.focus({ preventScroll: true });
  }

  /**
   * Where the focus lands on the strip after a selection has rebuilt it.
   *
   * The disclosure first — it is what was pressed and it is where another selection starts
   * from. It is not always there: a sample rate that empties the menu drops it, so choosing
   * No Effect at 192 kHz rebuilds a strip with no opener at all, and the focus would fall to
   * <body>. The FACE is the anchor then: it survives the ceiling (read-only, with its
   * reason) and it is the same control the disclosure sat beside. The strip's own root is
   * the floor under both, so a strip that carries neither still keeps the operator's place
   * rather than sending them back to the top of the document.
   */
  private focusInsFxAnchor(id: string): void {
    const root = this.refs.get(id)?.root;
    if (!root) return;
    const anchor =
      root.querySelector<HTMLElement>(".con-ifxopen") ?? root.querySelector<HTMLElement>(".con-ifxface") ?? root;
    anchor.focus();
    // Asked rather than assumed: whether an element takes focus is a property of how it was
    // built, and a chip that is read-only for one reason today may be built another way
    // tomorrow. If it did not take it, the strip root did.
    if (document.activeElement !== anchor) root.focus();
  }

  /** Repaint every A.GAIN for whether its jack's Clip Safe is holding the gain down. */
  clipSafeChanged(): void {
    for (const box of this.host.querySelectorAll<HTMLElement>(".con-gain[data-clip-safe-jack]"))
      this.paintClipSafe(box);
  }

  /** One A.GAIN box: while its jack's Clip Safe holds the gain down the label reads SAFE and the
   *  value and the knob take the Clip Safe colour. */
  private paintClipSafe(box: HTMLElement): void {
    const engaged = this.hooks.clipSafeEngaged?.(Number(box.dataset.clipSafeJack)) ?? false;
    box.classList.toggle("clip-safe", engaged);
    const lbl = box.querySelector<HTMLElement>(".lbl");
    if (lbl) {
      lbl.dataset.label ??= lbl.textContent ?? "";
      lbl.textContent = engaged ? t().console.clipSafeLabel : lbl.dataset.label;
    }
    const knob = box.querySelector<HTMLElement>(".con-knob");
    if (knob) {
      knob.dataset.description ??= knob.getAttribute("aria-description") ?? "";
      const said = [knob.dataset.description, engaged ? t().inspector.clipSafeEngaged : ""].filter(Boolean).join(" ");
      if (said) knob.setAttribute("aria-description", said);
      else knob.removeAttribute("aria-description");
    }
  }

  // Build a labelled rotary knob (label / value / knob) in the strip head and
  // wire it. Shared by the channel gain and the monitor PHONES level.
  // `midiId` makes the knob armable for MIDI learn.
  private addKnob(head: HTMLElement, label: string, k: KnobSpec, id: string, midiId?: string): HTMLElement {
    const box = el("div", "con-gain");
    const info = el("div", "info");
    const lbl = el("span", "lbl");
    lbl.textContent = label;
    // Fine-eligible knob: printed FINE legend; placement + arming live in
    // style.css (.con-gain .fine-tag / .fine-mode).
    if (k.fine !== undefined) {
      box.classList.add("has-fine");
      box.append(fineTag());
    }
    const { knob, val } = this.buildKnob(k, label, id, "val", midiId);
    info.append(lbl, val);
    box.append(info, knob);
    head.append(box);
    return knob;
  }

  // The knob primitive: the con-knob element + its value span (readonly / aria /
  // tabindex plumbing), wired via wireKnob. addKnob wraps it in the head's con-gain
  // box; the SEND PAN popover wraps it in a pcol (with a "rv" value class) and makes it
  // `horizontal`. A device-locked knob shows its value but takes no input (wireKnob
  // skips handlers).
  private buildKnob(
    k: KnobSpec,
    ariaLabel: string,
    id: string,
    valCls: string,
    midiId?: string,
    partnerSync = true,
    horizontal = false,
  ): { knob: HTMLElement; val: HTMLElement } {
    const knob = el("div", "con-knob" + (k.readonlyTitle ? " readonly" : ""));
    knob.setAttribute("role", "slider");
    knob.setAttribute("aria-label", ariaLabel);
    knob.setAttribute("aria-valuemin", String(k.min));
    knob.setAttribute("aria-valuemax", String(k.max));
    if (horizontal) knob.setAttribute("aria-orientation", "horizontal");
    knob.dataset.ctl = midiId ?? "knob:" + ariaLabel;
    knob.append(el("i", "ind"));
    const val = el("span", valCls);
    if (k.readonlyTitle) {
      knob.setAttribute("aria-disabled", "true");
      setTip(knob, k.readonlyTitle);
    } else {
      knob.tabIndex = 0;
    }
    this.wireKnob(knob, val, k, id, midiId, partnerSync, horizontal);
    return { knob, val };
  }

  // Rotary knob: vertical drag (≈ full range over KNOB_TRAVEL_PX) and arrow keys edit the
  // value (snapped to `step`, or to `grid` where the spec sets one); the indicator
  // rotates over a 270° sweep; a double-click resets to `reset`. Reads/writes via the
  // spec's get/set.
  // `partnerSync` (default on) re-renders after a linked-pair edit so the partner
  // strip's head knob catches up; the SEND PAN popover knob turns it OFF, since a
  // render would tear the popover down and no partner send-pan control is on screen
  // (the plan mirror via `commit` is enough). `horizontal` steps on Left / Right
  // alone and leaves Up / Down to the container; otherwise both axes step.
  private wireKnob(
    knob: HTMLElement,
    val: HTMLElement,
    k: KnobSpec,
    id: string,
    midiId?: string,
    partnerSync = true,
    horizontal = false,
  ): void {
    const angle = k.angle ?? ((v: number): number => -135 + ((v - k.min) / (k.max - k.min)) * 270);
    // Step for an interaction: fine while Shift is held — the event's own modifier
    // state OR the global tracker (covers Shift pressed away from the control).
    const stepFor = (ev?: { shiftKey: boolean }): number =>
      k.fine !== undefined && (ev?.shiftKey === true || fineActive()) ? k.fine : k.step;
    const show = (v: number): void => {
      val.textContent = k.format(v);
      knob.style.setProperty("--rot", angle(v) + "deg");
      knob.setAttribute("aria-valuenow", String(v));
      // The face's own text ("L63", "C", "+8"), which is what the number means.
      knob.setAttribute("aria-valuetext", k.format(v));
    };
    const write = (v: number): void => {
      k.set(v);
      show(v);
      this.commit(id, k.keys);
      this.mirrorHolderKnob(id, knob, val, k.keys);
      k.mirror?.(knob, val);
    };
    const apply = (raw: number, st = k.step): void => {
      const to = k.grid ?? st;
      write(Math.max(k.min, Math.min(k.max, scrubFloat(Math.round(scrubFloat(raw / to)) * to))));
    };
    // One step for a key or a wheel notch. On a knob with a `grid` it moves the held value by
    // exactly the step, so a value off the grid stays off it, and stops at the ends. Otherwise
    // it is snapped in the direction of travel: from a value between two grid points the next
    // point that way, never the nearest one past it. The drag and the double-click reset keep
    // `apply`'s nearest snap.
    const stepBy = (dir: 1 | -1, st: number): void => {
      if (k.grid !== undefined) {
        write(Math.max(k.min, Math.min(k.max, scrubFloat(k.get() + dir * st))));
        return;
      }
      const at = k.get() / st;
      apply((dir > 0 ? Math.floor(at + 1e-9) + 1 : Math.ceil(at - 1e-9) - 1) * st, st);
    };
    const syncPartner = (): void => {
      if (partnerSync) this.syncPartnerStrip(id, k.pairs);
    };
    const repaint = (): void => show(Math.max(k.min, Math.min(k.max, k.get())));
    this.knobRepaint.set(knob, repaint);
    repaint(); // initial display, not dirty
    if (k.readonlyTitle) {
      // device-locked: value painted, no input handlers — a learn target still where it is assignable
      if (k.assignable) this.learnOnly(knob, midiId);
      return;
    }
    this.midiMark(knob, midiId);
    knob.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      if (this.midiArm(midiId)) return;
      // Absolute drag mapping, with both anchors rebased whenever the Shift state
      // flips, so entering or leaving fine mode mid-drag never jumps the value:
      // each segment maps at its own rate — coarse = full range over KNOB_TRAVEL_PX, fine =
      // one fine step per pixel.
      let startY = e.clientY;
      let start = k.get();
      let wasSt = stepFor(e);
      trackDrag(
        knob,
        e,
        (ev) => {
          const st = stepFor(ev);
          if (st !== wasSt) {
            start = k.get();
            startY = ev.clientY;
            wasSt = st;
          }
          const rate = st === k.step ? (k.max - k.min) / KNOB_TRAVEL_PX : st;
          apply(start + (startY - ev.clientY) * rate, st);
        },
        { onEnd: syncPartner },
      );
    });
    knob.addEventListener("keydown", (e) => {
      if (this.midiLearnKey(e, midiId)) return;
      const st = stepFor(e);
      if (e.key === "ArrowRight" || (!horizontal && e.key === "ArrowUp")) stepBy(1, st);
      else if (e.key === "ArrowLeft" || (!horizontal && e.key === "ArrowDown")) stepBy(-1, st);
      else return;
      e.preventDefault();
      syncPartner();
    });
    knob.addEventListener("dblclick", () => {
      if (this.hooks.midi?.learnActive()) return; // pointerdown already armed
      apply(k.reset); // reset to factory value
      syncPartner();
    });
    // Hover + wheel nudges by one step (mirrors the Arrow keys). This sits below the
    // readonlyTitle early-return above, so device-locked knobs take no wheel input.
    onWheelStep(
      knob,
      (dir) => {
        stepBy(dir, stepFor());
        syncPartner();
      },
      () => this.hooks.midi?.learnActive(),
    );
  }
}

// APCA 0.98G-4g lightness contrast (Lc) between a text colour and a background,
// both #rrggbb as numbers. Unlike a WCAG ratio — which is two relative luminances
// divided — this carries polarity and the non-linearity of vision at the dark end,
// and that is exactly where the two disagree: on saturated mid-tones a ratio
// systematically picks the wrong ink. Measured across the device's ten CH SETTING
// colours plus the five node rails, the two verdicts differ on 12 of 21 grounds.
// Only the magnitude is used here, since which ink wins is the whole question.
function apcaLc(text: number, bg: number): number {
  const y = (n: number): number => {
    const ch = (c: number): number => Math.pow(c / 255, 2.4);
    return 0.2126729 * ch((n >> 16) & 255) + 0.7151522 * ch((n >> 8) & 255) + 0.072175 * ch(n & 255);
  };
  const soft = (v: number): number => (v > 0.022 ? v : v + Math.pow(0.022 - v, 1.414));
  const ty = soft(y(text));
  const by = soft(y(bg));
  if (Math.abs(by - ty) < 0.0005) return 0;
  // Normal polarity is dark text on a light ground; reverse polarity gets its own
  // exponents rather than a sign flip, which is the part a ratio cannot express.
  const raw =
    by > ty ? (Math.pow(by, 0.56) - Math.pow(ty, 0.57)) * 1.14 : (Math.pow(by, 0.65) - Math.pow(ty, 0.62)) * 1.14;
  const clamped = by > ty ? (raw < 0.1 ? 0 : raw - 0.027) : raw > -0.1 ? 0 : raw + 0.027;
  return Math.abs(clamped) * 100;
}

const INK_WHITE = { color: "#fff", shadow: "0 1px 1px rgba(0, 0, 0, 0.55)" };
const INK_DARK = { color: "#0e0c08", shadow: "0 1px 1px rgba(255, 255, 255, 0.5)" };

// Scribble label ink for a scribble ground: pick black or white by which reads
// better, and pair it with a faint opposite-tone halo so the small device name
// stays crisp even over a mid-tone colour neither ink clears cleanly. The halo is
// not decoration — four of the grounds this runs on reach Lc 60 with neither ink,
// and there the halo is the only thing holding the glyph edges.
function inkOn(hex: string): { color: string; shadow: string } {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  const bg = m ? parseInt(m[1], 16) : 0; // unparseable → black bg → white ink
  return apcaLc(0xffffff, bg) >= apcaLc(0x0e0c08, bg) ? INK_WHITE : INK_DARK;
}
